/* ---------------------------------------------------------------------------
   DEMONSTRATION DATA — PHILIPPINES, DPWH. TWO REAL RECORDS, INVENTED LIVES.

   The DPWH register is 108 real structures with six facts each and nothing
   behind them (src/data/dpwh.ts), so every application past the register shows
   an empty state. This file fills two of them in so the whole application can be
   SHOWN on the Daang Maharlika register, the way src/data/fwo-demo.ts does for
   Pakistan — and it is built the same way, behind a build flag and read through
   that file's exports, so the call sites are the ones it already has.

   ── WHAT IS REAL AND WHAT IS NOT ──

   REAL, and never overwritten: each record's asset id, name, contract package,
   station, length, lane count, desk-assessed condition and district, as the
   market-sounding pack lists them; and Afga's position, deck width, year and
   superstructure, as DPWH's Road & Bridge Inventory gives them. This file only
   FILLS WHAT THE REGISTER LEAVES EMPTY (`withDpwhDemo`). A value D1 holds always
   wins, so an edit somebody saves to one of these records shows through.

   INVENTED, which is everything else on both records: the watercourse names,
   spans, levels, every inventory-sheet field, the traffic figures, and every
   inspection, defect, rating, works entry, daily return, incident, document,
   test, register item, programme and contract. Every invented field reports its
   provenance as `demonstration` and carries the `D` mark (src/data/provenance.ts).

   ── THE TWO RECORDS ──

   ADLAYAN, B01798MN — UNDER CONSTRUCTION. The pack assesses the existing
   bridge as Poor, so this is its replacement under package A3: three 25 m spans
   of five pretensioned I-girders each, on two abutments on spread footings on
   weathered sandstone and two piers in the channel, each two columns on two
   four-pile caps of bored piles, with traffic on a temporary steel-panel detour
   downstream. 150 days into a 240-day contract: the old bridge is out, the
   abutments and both piers are up, the fifteen girders have been in the yard
   since day 91 waiting on bearings delivered a month late, and the last of
   span 1's went up yesterday. Slid onto today the way the Indus Relief Bridge
   is, so the current week always has work in it.

   AFGA, B00894MN — IN SERVICE, POSTED. The 1958 reinforced-concrete T-beam
   bridge the inventory describes: two 12 m spans of seven girders, widened to
   four lanes. Recorded in service in 2024 (LC-80); a principal inspection that
   August found main steel exposed and corroded on the soffit of exterior girder
   G1 in span 1, and the RCDG rating that followed it posts the bridge at the
   tonnes the real engine computes. A flood the next year scoured the pier.

   ── TO REMOVE ──

   Delete this file, the `VITE_DPWH_DEMO` line in run-philippines.bat and the
   default in vite.config.ts, the ph-dpwh branch in
   scripts/vite-plugin-local-api.ts, and every `dpwh` branch in
   src/data/fwo-demo.ts (each marked), the public/demo/defects folder and
   `RegisterDefect.photoUrls` with `api.defectPhotoSrc`'s use of it. Nothing
   here is written to D1 or R2.
   --------------------------------------------------------------------------- */

import type {
  Bridge,
  Inspection,
  ProgressEntry,
  RegisterDefect,
  SiteDocument,
  SiteIncident,
  SiteItem,
  SiteReturn,
  SiteTest,
} from './types.ts';
import type { Baseline, BaselineActivity, ScheduleMatch } from './variance.ts';
import { baselineFrom, baselineLinks, type SerialisedSchedule } from './programme.ts';
import type { WorkCalendar } from './calendar.ts';
import type { ContractExtension, ContractHeader } from './contract.ts';
import { idForLabel } from './componentTree.ts';
import { failedTestTitle, itemRef, testPasses } from './siteQuality.ts';
import type { PhaseTransition } from '../core/api.ts';
import { DPWH_CBPLR, eraRowFor, rateRcdgSimple, type RcdgInput } from '../cbplr/index.ts';
import type { RatingRecord } from '../rating/api.ts';
import { NO_LOSS, damageFromInspection } from '../rating/damage.ts';
import { postingTonnes } from './posting.ts';
import { BRIDGES_DPWH } from './dpwh.ts';

/* --------------------------- the flag --------------------------- */

/**
 * Whether the DPWH demonstration is in this build. `VITE_DPWH_DEMO`, read the
 * way `demoEnabled` in ./fwo-demo.ts reads its own flag: the build's value in a
 * browser, `process.env` under `npm test`. Default off.
 */
export function dpwhDemoEnabled(): boolean {
  return on(readViteEnv()) || on(readNodeEnv());
}

function on(value: string | undefined): boolean {
  return value === '1' || value === 'true';
}

function readViteEnv(): string | undefined {
  try {
    return (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_DPWH_DEMO;
  } catch {
    return undefined;
  }
}

function readNodeEnv(): string | undefined {
  try {
    return (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env
      ?.VITE_DPWH_DEMO;
  } catch {
    return undefined;
  }
}

/* --------------------------- shared --------------------------- */

const TENANT = 'ph-dpwh';
export const ADLAYAN_ID = 'B01798MN';
export const AFGA_ID = 'B00894MN';

/** The same local accounts the FWO demonstration names (LC-72). */
const PEOPLE = {
  planner: 'planner@local',
  site: 'site@local',
  resident: 'resident@local',
  inspector: 'inspector@local',
  engineer: 'engineer@local',
  rater: 'rater@local',
  approver: 'approver@local',
} as const;

const SCALE = '0-4 scale';
const WORKS_PREFIX = 'demo-works-';
const SITE_PREFIX = 'demo-site-';

/** Day 150 of the Adlayan contract, before sliding; see `shift`. */
const ANCHOR = '2026-09-20';
const LAST_DAY = 150;

function addDays(iso: string, days: number): string {
  const at = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  const day = at.toISOString().slice(0, 10);
  return iso.length > 10 ? `${day}${iso.slice(10)}` : day;
}

/** How far the Adlayan record slides for its newest day to land today. */
function shift(): number {
  const today = `${new Date().toISOString().slice(0, 10)}T00:00:00Z`;
  return Math.round((Date.parse(today) - Date.parse(`${ANCHOR}T00:00:00Z`)) / 86_400_000);
}

/** Day `n` of the Adlayan contract, before sliding. */
function day(n: number): string {
  return addDays(ANCHOR, n - LAST_DAY);
}

/** Day `n`, slid onto the week the page is opened in. */
function slidDay(n: number): string {
  return addDays(day(n), shift());
}

/* ===========================================================================
   THE INVENTORY — what each record gains where the register holds nothing.
   =========================================================================== */

const ADLAYAN: Partial<Bridge> = {
  roadName: 'Daang Maharlika (Pan-Philippine Highway)',
  river: 'Adlayan Creek',
  type: 'PSC I-girder, three simply supported spans (replacement)',
  spans: 3,
  spanLength: 25.0,
  deckWidth: 17.6,
  carriageway: 14.6,
  province: 'Agusan del Sur',
  deckRL: 48.6,
  hfl: 45.9,
  deckBearing: 152.0,
  inventoryCode: 'DMRP-A3-BR-1307',
  divisionOffice: 'Agusan del Sur 2nd District Engineering Office',
  designLoad: 'AASHTO HL-93 (DPWH DGCS 2015)',
  abutmentType: 'RC cantilever abutment with bearing beam and back wall',
  abutmentFoundation: 'Spread footing on weathered sandstone, 3.0 m below bed level',
  pierType: 'RC two-column bent with cap beam',
  pierFoundation: 'Two pile caps of four 1.0 m bored piles, socketed into sandstone',
  bearings: 'Laminated elastomeric, one under each girder end',
  railings: 'RC barrier with steel handrail, 1.2 m footway each side',
  movement: 'Strip-seal expansion joint at each abutment and pier',
  riverTraining: 'Grouted riprap slope protection at A1 and A2; riprap apron round both piers',
  runningSurface: '50 mm asphalt wearing course over RC deck slab',
  services: 'Ducts for street lighting in both footways',
  designedBy: 'Mindanao Bridge Consultants',
  constructedBy: 'Agusan Bridgeworks Corp.',
  aadt: 7400,
  aadtYear: 2025,
  detourKm: 0.4,
  detourMinutes: 3,
  lifeline: true,
  phase: 'construction',
  restricted: false,
  phaseCause: 'baseline',
};

const AFGA: Partial<Bridge> = {
  roadName: 'Daang Maharlika (Pan-Philippine Highway)',
  river: 'Afga Creek',
  spans: 2,
  spanLength: 12.0,
  carriageway: 14.0,
  deckRL: 62.4,
  hfl: 59.8,
  deckBearing: 141.0,
  inventoryCode: 'DMRP-A1-BR-1246',
  divisionOffice: 'Agusan del Sur 1st District Engineering Office',
  designLoad: 'AASHO H15-44 (as built, 1958)',
  abutmentType: 'RC gravity abutment with wing walls',
  abutmentFoundation: 'Spread footing on stiff clay',
  pierType: 'RC wall pier with rounded nose',
  pierFoundation: 'Spread footing, 2.5 m below bed level',
  bearings: 'Steel sliding plates at the pier and A2; fixed at A1',
  railings: 'RC post and rail; steel guardrail added at the 1994 widening',
  movement: 'Open joint with steel angle nosing at each support',
  riverTraining: 'Grouted riprap around the pier nose (2016)',
  runningSurface: '75 mm asphalt overlay over RC deck slab',
  services: '150 mm water main on the downstream fascia',
  aadt: 9800,
  aadtYear: 2025,
  detourKm: 46,
  detourMinutes: 70,
  lifeline: true,
  inServiceOn: '1958',
};

/* --------------------------- Afga's inspections --------------------------- */

type Element = NonNullable<Inspection['elements']>[number];
const el = (element: string, rating: number, extent: number, urgency: number): Element => ({ element, rating, extent, urgency });

const AFGA_INSPECTIONS: Inspection[] = [
  {
    id: 'INSP-PH-0894-A',
    date: '2019-08-14',
    type: 'principal',
    rating: 1,
    scale: SCALE,
    state: 'accepted',
    inspector: PEOPLE.inspector,
    reviewer: PEOPLE.engineer,
    reviewedAt: '2019-08-30T09:00:00Z',
    note: 'Fair order for its age. Hairline cracking in the A2 wall, map cracking in the overlay over the pier, and the open joints full of debris.',
    elements: [
      el('deck', 1, 1, 0), el('deck_surface', 1, 2, 1), el('bearings', 1, 1, 0), el('joints', 2, 2, 1),
      el('pier', 1, 1, 0), el('abutment', 1, 1, 0), el('parapet', 1, 1, 0), el('river_training', 1, 1, 0),
      { element: 'protection', rating: 'N' },
    ],
  },
  {
    id: 'INSP-PH-0894-B',
    date: '2021-09-02',
    type: 'routine',
    rating: 2,
    scale: SCALE,
    state: 'accepted',
    inspector: PEOPLE.inspector,
    reviewer: PEOPLE.engineer,
    reviewedAt: '2021-09-10T09:00:00Z',
    note: 'Rust staining along the soffit of the upstream girder in span 1. Potholing starting over span 2.',
  },
  {
    id: 'INSP-PH-0894-C',
    date: '2023-07-18',
    type: 'routine',
    rating: 2,
    scale: SCALE,
    state: 'accepted',
    inspector: PEOPLE.inspector,
    reviewer: PEOPLE.engineer,
    reviewedAt: '2023-07-27T09:00:00Z',
    note: 'Staining on G1 spreading, with a first spall at midspan. Recommended a principal inspection ahead of its cycle.',
  },
  {
    id: 'INSP-PH-0894-D',
    date: '2024-08-21',
    type: 'principal',
    rating: 3,
    scale: SCALE,
    state: 'accepted',
    inspector: PEOPLE.inspector,
    reviewer: PEOPLE.engineer,
    reviewedAt: '2024-09-03T10:30:00Z',
    note:
      'Spalling along the soffit of exterior girder G1 at midspan of span 1, six main bars exposed and ' +
      'corroded, about 12% section loss. Referred for a load rating. Overlay potholed over span 2.',
    elements: [
      el('deck', 3, 2, 3), el('deck_surface', 3, 3, 2), el('bearings', 2, 2, 1), el('joints', 3, 3, 2),
      el('pier', 2, 1, 1), el('abutment', 2, 1, 1), el('parapet', 2, 2, 1), el('river_training', 2, 2, 1),
      { element: 'protection', rating: 'N' },
    ],
  },
  {
    /* The routine visit that went with that principal, so the routine cycle
       counts from 2024 rather than reporting one overdue since 2022. */
    id: 'INSP-PH-0894-E',
    date: '2024-08-21',
    type: 'routine',
    rating: 3,
    scale: SCALE,
    state: 'accepted',
    inspector: PEOPLE.inspector,
    reviewer: PEOPLE.engineer,
    reviewedAt: '2024-09-03T10:30:00Z',
    note: 'Walked with the principal inspection party the same day.',
  },
  {
    id: 'INSP-PH-0894-F',
    date: '2025-11-05',
    type: 'special',
    trigger: 'flood',
    rating: 3,
    scale: SCALE,
    state: 'accepted',
    inspector: PEOPLE.inspector,
    reviewer: PEOPLE.engineer,
    reviewedAt: '2025-11-12T08:30:00Z',
    note: 'After the flood of 2–3 November. Scour hole at the upstream nose of the pier, riprap displaced; the footing not undermined at the points probed.',
    elements: [
      el('pier', 3, 2, 3), el('river_training', 3, 3, 3), el('abutment', 2, 1, 1),
    ],
  },
  {
    id: 'INSP-PH-0894-G',
    date: '2026-08-12',
    type: 'routine',
    rating: 3,
    scale: SCALE,
    state: 'accepted',
    inspector: PEOPLE.inspector,
    reviewer: PEOPLE.engineer,
    reviewedAt: '2026-08-20T09:00:00Z',
    note: 'G1 spall unchanged since 2024. Span 2 overlay patched in March 2025 and holding. Pier scour not yet treated.',
  },
];

/* --------------------------- Afga's defects --------------------------- */

/**
 * The defect's stand-in photograph: a Commons photograph of the same kind of
 * defect elsewhere, named in public/demo/defects/SOURCES.md. NONE IS OF AFGA.
 */
const photosOf = (id: string) => ({
  photoUrls: [`./demo/defects/afga-${id}.jpg`],
  photoThumbUrls: [`./demo/defects/afga-${id}-t.jpg`],
});

/**
 * On the model uploaded for Afga (m-1790851013969, revision 48755faf…): each
 * component is that export's own name and GUID, as a pick on the record's 3D
 * view writes them, and each pin is a point on that component's surface in the
 * `.glb`'s frame — so the Defects card files them under the model's tree and the
 * 3D view draws them where they are. A different model of Afga renames nothing
 * here; the rows fall back under "Not on this model", as any stranded defect does.
 */
const AFGA_DEFECTS: RegisterDefect[] = [
  {
    id: 'dfp0001',
    clientId: 'demo-ph-0001',
    bridgeId: AFGA_ID,
    element: 'deck',
    component: 'G-1',
    componentId: '494d5c16-85ec-5f29-be97-3e97a91d3f02',
    modelDefectId: 1,
    code: 'REE',
    severity: 'Severe',
    status: 'Open',
    note: 'Spalled cover along the soffit of exterior girder G-1 at midspan of span 1. Six of the eight main bars exposed and corroded, about 12% of their section lost.',
    loggedAt: '2024-08-21',
    author: PEOPLE.inspector,
    inspectionId: 'INSP-PH-0894-D',
    pin: { x: 12.5, y: -2.05, z: 5 },
    normal: { x: 0, y: -1, z: 0 },
    hasPhoto: true,
    ...photosOf('dfp0001'),
    quantity: { method: 'measured', instrument: 'Tape and vernier on cleaned bars', areaM2: 2.4, count: 6, sectionLossPct: 12 },
  },
  {
    id: 'dfp0002',
    clientId: 'demo-ph-0002',
    bridgeId: AFGA_ID,
    element: 'pier',
    component: 'F-2',
    componentId: 'de13889c-53b1-5b67-8d9b-2967b08ca72d',
    modelDefectId: 2,
    code: 'SCO',
    severity: 'Severe',
    status: 'In Review',
    note: 'Scour hole at the upstream face of pile cap F-2 under Bent-1 after the November 2025 flood; riprap displaced downstream. Piles not exposed where probed.',
    loggedAt: '2025-11-05',
    author: PEOPLE.inspector,
    inspectionId: 'INSP-PH-0894-F',
    pin: { x: 25, y: -10.6, z: 6.25 },
    normal: { x: 0, y: 0, z: 1 },
    hasPhoto: true,
    ...photosOf('dfp0002'),
    quantity: { method: 'measured', instrument: 'Sounding rod from a boat', depthM: 1.4, lengthM: 6.5, volumeM3: 18 },
  },
  {
    id: 'dfp0003',
    clientId: 'demo-ph-0003',
    bridgeId: AFGA_ID,
    element: 'deck_surface',
    component: 'TrafficLane 2',
    componentId: 'bd33f681-fd4e-55bb-a13f-be3dbbc43b72',
    modelDefectId: 3,
    code: 'POH',
    severity: 'Moderate',
    status: 'Repaired',
    note: 'Seven potholes through the overlay over span 2, northbound lanes.',
    loggedAt: '2024-08-21',
    author: PEOPLE.inspector,
    inspectionId: 'INSP-PH-0894-D',
    pin: { x: 36, y: 0.06, z: 2.2 },
    normal: { x: 0, y: 1, z: 0 },
    hasPhoto: true,
    ...photosOf('dfp0003'),
    quantity: { method: 'measured', instrument: 'Tape', count: 7, areaM2: 5.2, depthMm: 70 },
    repair: { on: '2025-03-10', ref: 'DEO-ADS1-MW-2025-014', by: PEOPLE.engineer },
    timeline: [
      { on: '2024-08-21', status: 'Open' },
      { on: '2025-03-10', status: 'Repaired' },
    ],
  },
  {
    id: 'dfp0004',
    clientId: 'demo-ph-0004',
    bridgeId: AFGA_ID,
    element: 'joints',
    component: 'PSC Girders-1',
    componentId: '36bd3107-8580-5409-aa1d-3d63e71e92de',
    modelDefectId: 4,
    code: 'SEP',
    severity: 'Moderate',
    status: 'Open',
    note: 'Open joint over Bent-1 packed with debris; angle nosing loose over the southbound lanes.',
    loggedAt: '2024-08-21',
    author: PEOPLE.inspector,
    inspectionId: 'INSP-PH-0894-D',
    pin: { x: 25.4, y: 0, z: -3 },
    normal: { x: 0, y: 1, z: 0 },
    hasPhoto: true,
    ...photosOf('dfp0004'),
    quantity: { method: 'measured', instrument: 'Tape', widthMm: 45, lengthM: 16.2 },
  },
  {
    id: 'dfp0005',
    clientId: 'demo-ph-0005',
    bridgeId: AFGA_ID,
    element: 'parapet',
    component: 'Span-2',
    componentId: '6f947e8b-9799-5615-bbcd-7c0ad086cfc3',
    modelDefectId: 5,
    code: 'SPA',
    severity: 'Minor',
    status: 'Open',
    note: 'Two railing posts spalled by a vehicle strike on the downstream barrier of span 2.',
    loggedAt: '2026-08-12',
    author: PEOPLE.inspector,
    inspectionId: 'INSP-PH-0894-G',
    pin: { x: 39, y: 0.6, z: -7.5 },
    normal: { x: 0, y: 0, z: -1 },
    hasPhoto: true,
    ...photosOf('dfp0005'),
    quantity: { method: 'measured', instrument: 'Tape', areaM2: 0.4, depthMm: 60 },
  },
  {
    id: 'dfp0006',
    clientId: 'demo-ph-0006',
    bridgeId: AFGA_ID,
    element: 'abutment',
    component: 'Front Wall',
    componentId: 'f34b4350-7e83-5a51-980a-313f7b0dcb0b',
    modelDefectId: 6,
    code: 'CRK',
    severity: 'Minor',
    status: 'Open',
    note: 'Vertical crack in the Abutment-2 front wall below the bearing shelf, first noted in 2019 and slowly widening.',
    loggedAt: '2019-08-14',
    author: PEOPLE.inspector,
    inspectionId: 'INSP-PH-0894-A',
    pin: { x: 74.1, y: -4.5, z: 1.2 },
    normal: { x: -1, y: 0, z: 0 },
    hasPhoto: true,
    ...photosOf('dfp0006'),
    quantity: { method: 'measured', instrument: 'Crack width gauge', lengthM: 2.8, widthMm: 0.6 },
  },
  {
    id: 'dfp0007',
    clientId: 'demo-ph-0007',
    bridgeId: AFGA_ID,
    element: 'deck',
    component: 'PSC Girders-1',
    componentId: '36bd3107-8580-5409-aa1d-3d63e71e92de',
    modelDefectId: 7,
    code: 'CRK',
    severity: 'Minor',
    status: 'Resolved',
    note: 'Transverse hairline cracks with efflorescence in the span 2 slab soffit between G-3 and G-4. Inactive; recorded and closed.',
    loggedAt: '2021-09-02',
    author: PEOPLE.inspector,
    inspectionId: 'INSP-PH-0894-B',
    pin: { x: 40, y: -0.22, z: -1.25 },
    normal: { x: 0, y: -1, z: 0 },
    hasPhoto: true,
    ...photosOf('dfp0007'),
    quantity: { method: 'measured', instrument: 'Crack width gauge', lengthM: 9.5, widthMm: 0.2 },
  },
];

/* --------------------------- Afga's rating --------------------------- */

const AFGA_DAMAGE = damageFromInspection(
  { id: AFGA_ID, inspections: AFGA_INSPECTIONS },
  AFGA_DEFECTS,
  { family: 'RCDG', position: 'exterior', spanIndex: 1, girderCount: 7 },
);

const CONCRETE_1958 = eraRowFor(DPWH_CBPLR.eras.concrete, 1958)!;
const REBAR_1958 = eraRowFor(DPWH_CBPLR.eras.rebar, 1958)!;

/**
 * Exterior girder G1 of span 1: a 450 × 900 mm web under a 200 mm slab, seven
 * girders at 2.4 m on the 16.2 m deck, eight 32 mm bars. Materials from the
 * year-built tables (1958), and the section loss is the register's — dfp0001,
 * filled the way the rating form fills it.
 */
const AFGA_GIRDER: RcdgInput = {
  position: 'exterior',
  span: 11.6,
  girderWidth: 0.45,
  webHeight: 0.9,
  filletWidth: 0.1,
  filletHeight: 0.1,
  slabThickness: 0.2,
  girderSpacing: 2.4,
  girderCount: 7,
  barrierRail: 3.6,
  wearingCourse: 75,
  concreteUnitWeight: 24,
  asphaltUnitWeight: 22,
  cantilever: { thickness: 0.18, haunch: 0.05, length: 0.9, railingWidth: 1.0 },
  steelArea: 6434,
  centroidFromBottom: 120,
  sectionLoss: AFGA_DAMAGE.fields['sectionLoss'] ?? 0,
  stirrupArea: 113.1,
  stirrupSpacing: 200,
  stirrupSectionLoss: 0,
  shearCrackDepth: 0,
  concrete: { strength: CONCRETE_1958.strength, allowable: { inventory: CONCRETE_1958.allowable.inventory, operating: CONCRETE_1958.allowable.operating } },
  rebar: { strength: REBAR_1958.strength, allowable: { inventory: REBAR_1958.allowable.inventory, operating: REBAR_1958.allowable.operating } },
};

function afgaSources(): Record<string, string> {
  const sources: Record<string, string> = {};
  for (const key of Object.keys(AFGA_GIRDER)) {
    if (key === 'position' || key === 'cantilever' || key === 'concrete' || key === 'rebar') continue;
    sources[key] = 'measured';
  }
  for (const key of ['thickness', 'haunch', 'length', 'railingWidth']) sources[`cantilever.${key}`] = 'measured';
  for (const key of ['strength', 'allowable.inventory', 'allowable.operating']) {
    sources[`concrete.${key}`] = 'era';
    sources[`rebar.${key}`] = 'era';
  }
  sources['concreteUnitWeight'] = 'standard';
  sources['asphaltUnitWeight'] = 'standard';
  sources['sectionLoss'] = 'inspection';
  sources['stirrupSectionLoss'] = 'inspection';
  sources['shearCrackDepth'] = 'inspection';
  return sources;
}

function afgaRating(): RatingRecord {
  const result = rateRcdgSimple(AFGA_GIRDER, DPWH_CBPLR).result;
  const g = result.governing;
  return {
    id: 'demo-rating-ph-0894-1',
    bridgeId: AFGA_ID,
    family: 'RCDG',
    continuity: 'simple',
    position: 'exterior',
    spanIndex: 1,
    profileId: DPWH_CBPLR.id,
    profileVersion: DPWH_CBPLR.version,
    engineVersion: 'rcdg-simple',
    exteriorDf: DPWH_CBPLR.exteriorDf,
    input: AFGA_GIRDER,
    sources: afgaSources(),
    result,
    corrections: [],
    governingRf: g?.rf ?? null,
    governingCheck: g?.checkLabel ?? null,
    governingMethod: g?.method ?? null,
    governingLevel: g?.level ?? null,
    postingRequired: result.postingRequired,
    inspectionId: AFGA_DAMAGE.inspectionId,
    defectIds: AFGA_DAMAGE.defectIds,
    damageBasis: { ...AFGA_DAMAGE.basis, stirrupSectionLoss: NO_LOSS, shearCrackDepth: NO_LOSS },
    state: 'accepted',
    ratedBy: PEOPLE.rater,
    ratedAt: '2024-10-02T09:30:00Z',
    acceptedBy: PEOPLE.approver,
    acceptedAt: '2024-10-15T14:00:00Z',
    note:
      'Span 1, exterior girder G1. Section measured on site — no drawings survive — and materials from the ' +
      'year-built tables. Main steel taken at the 12% loss the August 2024 principal inspection measured (dfp0001).',
  };
}

/* The posting is the rating's (LC-23), set from it so the two cannot disagree. */
{
  const rating = afgaRating();
  AFGA.loadRestriction = postingTonnes(rating.result);
  AFGA.posting = { source: 'rating', ratingId: rating.id, by: rating.acceptedBy, at: rating.acceptedAt };
  AFGA.phase = 'in-service';
  AFGA.restricted = rating.postingRequired;
  AFGA.phaseAt = rating.acceptedAt;
  AFGA.phaseBy = rating.acceptedBy;
  AFGA.phaseCause = 'rating';
  AFGA.inspections = AFGA_INSPECTIONS;
}

/* ===========================================================================
   ADLAYAN — THE CONSTRUCTION RECORD
   =========================================================================== */

/**
 * The bank-run photographs the FWO demonstration carries, by activity — real
 * construction photographs of other bridges, from Wikimedia Commons, credited in
 * public/demo/progress/SOURCES.md. NONE IS OF ADLAYAN. Each is given to one
 * Adlayan entry of the same activity, first come first served.
 */
const PHOTO_POOL: Readonly<Record<string, readonly number[]>> = {
  'survey-check': [2, 106],
  'setting-out': [12, 14],
  excavation: [17, 20, 21, 26, 38, 56],
  'hold-point': [20, 42, 99, 126, 140],
  'rebar-fixed': [23, 131],
  dewatering: [24, 55],
  'footing-poured': [27, 66],
  'pile-concreted': [28, 29, 30, 33, 36, 43],
  'pilecap-poured': [50, 73],
  stoppage: [49, 50],
  'girder-cast': [52],
  'formwork-erected': [57, 70, 134],
  'concrete-poured': [59, 80, 82, 138],
  'non-conformance': [101],
  'bearing-set': [108, 124, 134],
  'formwork-struck': [126],
  'girder-erected': [127, 128],
  'diaphragm-cast': [137],
};

type WorkRow = [
  day: number,
  activity: string,
  element: string | null,
  component: string | null,
  quantity: number | null,
  unit: string | null,
  note: string,
  componentId?: string,
];

/**
 * The parts of Adlayan's model the record names one at a time, by the GUIDs
 * its CSiBridge export gives them (Multi Span Overpass, model 6d07a268…). They
 * are the same in every export of the model, so a re-export keeps the record
 * placed; on any other model the entries fall back to their place in words.
 */
const PART = {
  a1Footing: 'a78362ad-2311-51a6-a87b-f20e56731e2a',
  a1FrontWall: 'a5624e9f-279f-5197-a82b-f31b63fde87c',
  a1BearingBeam: 'bfc2980a-2a20-553a-bbbc-0d53ddb0beff',
  a2Footing: '3f4a2a57-bffb-50cd-956b-e46deff8c75d',
  a2FrontWall: '44fc654e-f8e7-5986-9a84-4f4f0f2c0afc',
  a2BearingBeam: 'bdb984a9-4a96-5ff5-b302-e2465964bc3d',
} as const;

/** Each pier's piles (by cap), pile caps, columns and bent cap. */
const PIERS = [
  {
    piles: [
      ['10ece888-b26c-5a27-b010-c310ad512c70', '4cb56a87-117a-5619-9f4a-46df8902d961', '4a7b75d7-b1ae-5e10-8aa0-431280acd01d', '7159bf12-cbec-5c91-96f4-3e0cb2f57f09'],
      ['d8a34c85-513f-5b02-b895-add2c97bb195', '784538e0-ae9a-54eb-b178-d3bbd2965e5b', '4000c653-73d6-56d4-814e-e80fa2056a7c', '687f7e49-0445-5b29-a0d4-a41efa0dd688'],
    ],
    caps: ['d8e103a1-a5b5-576c-b789-bf6b459824d3', '25bf9ebd-5c8b-5362-9e72-45d52c2320f3'],
    columns: ['c798b8c8-129e-5d3b-807f-e74c6ffcb1d0', '39d7755b-75c6-5a47-b470-07222a7f54df'],
    bentCap: 'a6caa0ed-f007-5672-8f13-baeb0126fdd8',
  },
  {
    piles: [
      ['62fd5c30-0376-5b30-b7a8-7ea486dda2a9', '63615315-8b79-509d-afa0-e63bf73d0f63', '3f144387-2666-59a1-965c-ed72f8e06237', 'c3ec1ed8-d4ac-5bf2-905c-7c71d7e48c4d'],
      ['e0903745-612f-5b16-b877-6e6d4c232e8d', '0e6adeb3-7ace-5581-a963-25ba7a6213c3', '292061b9-8edb-5c16-a964-83f8e288f1e7', '8064e366-8fb2-5a14-8151-9d89c688bc81'],
    ],
    caps: ['a2b64382-1036-5f27-b532-f9bdd21aec49', '7bcc9e76-d8e7-5b24-a0ce-c5166dcb4764'],
    columns: ['8396e6dc-5b75-58bf-83d8-099773c7d997', 'f1cb6fa2-bd83-569b-b244-5b2eccb5c7ea'],
    bentCap: '95d657d7-7b56-5ac6-99bd-5bb603af14ad',
  },
] as const;

/** Span 1's girders G-1 to G-5. */
const SPAN1_GIRDERS = [
  'e640b23d-c2ea-59de-9617-741414cce768',
  '03176413-05c2-50a3-b269-fa51ca9787b4',
  'cc30e47f-51ef-579e-a7a5-4ccbc39f60f1',
  'f9a4066b-ec20-52ac-b7f7-f0a10538309f',
  '4ead6d05-340c-573c-af97-1f9100b77686',
] as const;

const pile = (pier: 1 | 2, cap: 1 | 2, n: 1 | 2 | 3 | 4): string => PIERS[pier - 1]!.piles[cap - 1]![n - 1]!;
const pileCap = (pier: 1 | 2, cap: 1 | 2): string => PIERS[pier - 1]!.caps[cap - 1]!;
const column = (pier: 1 | 2, c: 1 | 2): string => PIERS[pier - 1]!.columns[c - 1]!;
const bentCap = (pier: 1 | 2): string => PIERS[pier - 1]!.bentCap;
const girder = (n: number) => SPAN1_GIRDERS[n - 1]!;

/**
 * 150 days of the Adlayan replacement, every entry invented. Paced as concrete
 * allows — footings three days old before a wall starts, piles integrity-tested
 * a week after the last, columns not before their cap is five days old, seven
 * days' curing on every pour, girders released at five days on transfer-strength
 * cylinders — and late as contracts are: the creek in flood over the A2
 * excavation and the P2 cofferdam, a soft pocket under the A2 footing, and
 * bearings a month late, so the girders stood in the yard until day 145.
 */
const ADLAYAN_ROWS: readonly WorkRow[] = [
  /* --- mobilisation and the detour --- */
  [2, 'survey-check', null, 'Site establishment', null, null, 'Joint survey of the site, the old bridge and the detour corridor with the Project Engineer.'],
  [5, 'setting-out', null, 'All supports', null, null, 'Centre lines of A1, A2 and the detour set out from the DMRP control stations.'],
  [6, 'survey-check', null, 'All supports', null, null, 'Setting out checked by the Resident Engineer; worst closure 5 mm.'],
  [8, 'setting-out', null, 'Casting yard', null, null, 'Casting yard and the pretensioning bed set out on the A1 side, north of the creek.'],
  [9, 'excavation', null, 'Temporary detour', 420, 'm³', 'Detour approaches cut on the downstream side.'],
  [13, 'backfill', null, 'Temporary detour', 860, 'm³', 'Detour embankments filled and compacted in 200 mm layers.'],
  [14, 'hold-point', null, 'Site establishment', null, null, 'Field office, laboratory, stores and batching plant inspected and accepted.'],
  [24, 'girder-erected', null, 'Temporary detour', 1, 'nos', 'Two-span steel panel bridge launched across the creek on the detour line.'],
  [30, 'wearing-course', null, 'Temporary detour', 640, 'm²', 'Detour approaches sealed; signs and barriers placed.'],
  [33, 'hold-point', null, 'Temporary detour', null, null, 'Detour inspected and opened to traffic. Old bridge closed at 18:00.'],
  [34, 'hold-point', null, 'Casting yard', null, null, 'Casting bed, two steel moulds and the stressing abutments accepted by the Engineer.'],

  /* --- the old bridge out --- */
  [35, 'excavation', null, 'Old bridge removal', 110, 'm³', 'Overlay and deck slab of the old bridge broken out.'],
  [38, 'excavation', null, 'Old bridge removal', 160, 'm³', 'Old girders saw-cut and lifted out by the 50 t crane.'],
  [42, 'excavation', null, 'Old bridge removal', 240, 'm³', 'Old abutments broken out to 1 m below bed level.'],
  [44, 'stoppage', null, 'Old bridge removal', null, null, 'Thunderstorm from 13:00; breaker and crane stood down.'],
  [47, 'hold-point', null, 'Old bridge removal', null, null, 'Channel cleared of debris; removal of the old foundations accepted.'],

  /* --- abutment A1, north bank --- */
  [48, 'excavation', 'abutment', 'Abutment A1 excavation', 380, 'm³', 'A1 excavation started inside a cofferdam of sandbags and sheeting.'],
  [49, 'dewatering', 'abutment', 'Abutment A1 excavation', null, null, 'A1 excavation pumped down; one 4-inch pump running.'],
  [51, 'excavation', 'abutment', 'Abutment A1 excavation', 290, 'm³', 'A1 excavation taken down through the river gravels.'],
  [53, 'stoppage', 'abutment', 'Abutment A1 excavation', null, null, 'Excavator hydraulic hose burst at 08:30; stood for the day awaiting a fitter from Butuan.'],
  [55, 'excavation', 'abutment', 'Abutment A1 excavation', 170, 'm³', 'A1 excavation reaches weathered sandstone at 3.1 m below bed level; the last 300 mm trimmed by hand.'],
  [57, 'hold-point', 'abutment', 'Abutment A1 formation', null, null, 'A1 formation inspected by the Resident Engineer and the designer’s geologist: weathered sandstone across the whole footprint. Accepted.', PART.a1Footing],
  [58, 'survey-check', 'abutment', 'Abutment A1 formation', null, null, 'A1 formation levels surveyed on a 2 m grid; worst deviation 25 mm low, made up in the blinding.'],
  [66, 'rebar-fixed', 'abutment', 'Abutment A1 footing', 14.8, 't', 'A1 blinding cast and the footing reinforcement fixed on it.', PART.a1Footing],
  [68, 'formwork-erected', 'abutment', 'Abutment A1 footing', 72, 'm²', 'A1 footing edge forms erected.', PART.a1Footing],
  [70, 'footing-poured', 'abutment', 'Abutment A1 footing', 168, 'm³', 'A1 footing poured, Class A, 05:00 to 12:30. Cylinders taken for 7 and 28 days.', PART.a1Footing],
  [77, 'concrete-cured', 'abutment', 'Abutment A1 footing', null, null, 'A1 footing cured seven days under wet burlap.', PART.a1Footing],
  [73, 'rebar-fixed', 'abutment', 'Abutment A1 front wall', 10.6, 't', 'A1 front wall cage fixed on the footing starters.', PART.a1FrontWall],
  [77, 'formwork-erected', 'abutment', 'Abutment A1 front wall', 146, 'm²', 'A1 front wall forms erected and plumbed.', PART.a1FrontWall],
  [80, 'concrete-poured', 'abutment', 'Abutment A1 front wall', 112, 'm³', 'A1 front wall poured in one lift.', PART.a1FrontWall],
  [82, 'formwork-struck', 'abutment', 'Abutment A1 front wall', 146, 'm²', 'A1 front wall forms struck at 48 hours.', PART.a1FrontWall],
  [82, 'non-conformance', 'abutment', 'Abutment A1 front wall', null, null, 'NCR-002: honeycombing over 0.6 m² at the base of the A1 front wall where the kicker leaked. Repair by approved polymer mortar proposed.', PART.a1FrontWall],
  [85, 'hold-point', 'abutment', 'Abutment A1 front wall', null, null, 'A1 front wall repair inspected; NCR-002 closed by the Resident Engineer.', PART.a1FrontWall],
  [87, 'concrete-cured', 'abutment', 'Abutment A1 front wall', null, null, 'A1 front wall cured seven days.', PART.a1FrontWall],
  [84, 'rebar-fixed', 'abutment', 'Abutment A1 wing walls', 6.2, 't', 'Both A1 wing wall cages fixed.'],
  [88, 'rebar-fixed', 'abutment', 'Abutment A1 bearing beam', 3.6, 't', 'A1 bearing beam and plinth reinforcement fixed.', PART.a1BearingBeam],
  [90, 'formwork-erected', 'abutment', 'Abutment A1 bearing beam', 38, 'm²', 'A1 bearing beam forms erected.', PART.a1BearingBeam],
  [92, 'concrete-poured', 'abutment', 'Abutment A1 bearing beam', 26, 'm³', 'A1 bearing beam and five plinths poured.', PART.a1BearingBeam],
  [99, 'concrete-cured', 'abutment', 'Abutment A1 bearing beam', null, null, 'A1 bearing beam cured seven days.', PART.a1BearingBeam],
  [94, 'formwork-erected', 'abutment', 'Abutment A1 wing walls', 150, 'm²', 'A1 wing wall forms erected.'],
  [97, 'concrete-poured', 'abutment', 'Abutment A1 wing walls', 64, 'm³', 'Both A1 wing walls poured.'],
  [104, 'concrete-cured', 'abutment', 'Abutment A1 wing walls', null, null, 'A1 wing walls cured seven days.'],
  [113, 'survey-check', 'bearings', 'Abutment A1 bearings', null, null, 'A1 plinth levels surveyed; worst deviation 2 mm. Bearings still not delivered.'],
  [140, 'bearing-set', 'bearings', 'Abutment A1 bearings', 5, 'nos', 'Five elastomeric bearings set on the A1 plinths on epoxy mortar, the day after delivery.'],

  /* --- abutment A2, south bank --- */
  [59, 'excavation', 'abutment', 'Abutment A2 excavation', 360, 'm³', 'A2 excavation started inside its cofferdam.'],
  [60, 'dewatering', 'abutment', 'Abutment A2 excavation', null, null, 'A2 excavation pumped down; seepage heavier than at A1, second pump brought in.'],
  [62, 'excavation', 'abutment', 'Abutment A2 excavation', 280, 'm³', 'A2 excavation taken down through the river gravels.'],
  [64, 'stoppage', 'abutment', 'Abutment A2 excavation', null, null, '92 mm of rain overnight; the creek rose 1.6 m, overtopped the cofferdam and flooded the A2 excavation. Plant withdrawn at 04:00.'],
  [65, 'stoppage', 'abutment', 'Abutment A2 excavation', null, null, 'Creek still high. No work at A2.'],
  [66, 'dewatering', 'abutment', 'Abutment A2 excavation', null, null, 'Creek down 1.2 m; A2 excavation pumped out and the bottom cleared of silt.'],
  [67, 'excavation', 'abutment', 'Abutment A2 excavation', 150, 'm³', 'A2 excavation reaches founding level, 3.0 m below bed level.'],
  [67, 'non-conformance', 'abutment', 'Abutment A2 formation', null, null, 'NCR-001: formation inspection finds a pocket of soft clay about 3 m by 4 m under the upstream end of the A2 footprint. Designer’s instruction sought.', PART.a2Footing],
  [69, 'excavation', 'abutment', 'Abutment A2 excavation', 48, 'm³', 'Soft pocket dug out to sandstone, 1.4 m below formation, on the designer’s instruction.'],
  [71, 'backfill', 'abutment', 'Abutment A2 excavation', 46, 'm³', 'Pocket filled to formation level with lean concrete, Class C, in one pour.'],
  [77, 'survey-check', 'abutment', 'Abutment A2 formation', null, null, 'A2 formation levels surveyed over the filled pocket and the sandstone around it.'],
  [86, 'hold-point', 'abutment', 'Abutment A2 formation', null, null, 'Lean-concrete fill probed at 14 days and accepted by the designer; A2 formation accepted and NCR-001 closed.', PART.a2Footing],
  [87, 'rebar-fixed', 'abutment', 'Abutment A2 footing', 14.8, 't', 'A2 blinding cast and the footing reinforcement fixed on it.', PART.a2Footing],
  [89, 'formwork-erected', 'abutment', 'Abutment A2 footing', 72, 'm²', 'A2 footing edge forms erected.', PART.a2Footing],
  [91, 'footing-poured', 'abutment', 'Abutment A2 footing', 168, 'm³', 'A2 footing poured, Class A.', PART.a2Footing],
  [98, 'concrete-cured', 'abutment', 'Abutment A2 footing', null, null, 'A2 footing cured seven days.', PART.a2Footing],
  [94, 'rebar-fixed', 'abutment', 'Abutment A2 front wall', 10.6, 't', 'A2 front wall cage fixed.', PART.a2FrontWall],
  [98, 'formwork-erected', 'abutment', 'Abutment A2 front wall', 146, 'm²', 'A2 front wall forms erected.', PART.a2FrontWall],
  [101, 'concrete-poured', 'abutment', 'Abutment A2 front wall', 112, 'm³', 'A2 front wall poured in one lift.', PART.a2FrontWall],
  [103, 'formwork-struck', 'abutment', 'Abutment A2 front wall', 146, 'm²', 'A2 front wall forms struck at 48 hours.', PART.a2FrontWall],
  [108, 'concrete-cured', 'abutment', 'Abutment A2 front wall', null, null, 'A2 front wall cured seven days.', PART.a2FrontWall],
  [106, 'rebar-fixed', 'abutment', 'Abutment A2 wing walls', 6.2, 't', 'Both A2 wing wall cages fixed.'],
  [109, 'rebar-fixed', 'abutment', 'Abutment A2 bearing beam', 3.6, 't', 'A2 bearing beam reinforcement fixed.', PART.a2BearingBeam],
  [110, 'formwork-erected', 'abutment', 'Abutment A2 wing walls', 150, 'm²', 'A2 wing wall forms erected.'],
  [111, 'formwork-erected', 'abutment', 'Abutment A2 bearing beam', 38, 'm²', 'A2 bearing beam forms erected.', PART.a2BearingBeam],
  [112, 'concrete-poured', 'abutment', 'Abutment A2 wing walls', 64, 'm³', 'Both A2 wing walls poured. Cylinders taken for 7 and 28 days.'],
  [114, 'concrete-poured', 'abutment', 'Abutment A2 bearing beam', 26, 'm³', 'A2 bearing beam and plinths poured.', PART.a2BearingBeam],
  [119, 'concrete-cured', 'abutment', 'Abutment A2 wing walls', null, null, 'A2 wing walls cured seven days.'],
  [121, 'concrete-cured', 'abutment', 'Abutment A2 bearing beam', null, null, 'A2 bearing beam cured seven days.', PART.a2BearingBeam],
  [128, 'survey-check', 'bearings', 'Abutment A2 bearings', null, null, 'A2 plinth levels surveyed; worst deviation 3 mm.'],
  [142, 'bearing-set', 'bearings', 'Abutment A2 bearings', 5, 'nos', 'Five bearings set on the A2 plinths.'],

  /* --- the causeway the piers are built from --- */
  [48, 'backfill', null, 'Temporary causeway', 1250, 'm³', 'Rock causeway pushed out from the A1 bank to the P1 and P2 positions, three 1.2 m culverts left through it for the creek.'],
  [51, 'hold-point', null, 'Temporary causeway', null, null, 'Causeway and piling platforms inspected; ground bearing for the rig accepted.'],

  /* --- pier P1, off the A1 bank --- */
  [52, 'excavation', 'pier', 'Pier P1 cofferdam', 120, 'm³', 'Sheet-pile cofferdam at P1 driven and closed; excavation to pile cap soffit begun.'],
  [53, 'dewatering', 'pier', 'Pier P1 cofferdam', null, null, 'P1 cofferdam pumped down; two 4-inch pumps running.'],
  [54, 'excavation', 'pier', 'Pier P1 cofferdam', 80, 'm³', 'P1 excavation at pile cap soffit. Piling rig walked out along the causeway.'],
  [55, 'hold-point', 'pier', 'Pier P1 piles', null, null, 'Piling rig, temporary casings and tremie inspected; pile positions checked by the Resident Engineer.'],
  [56, 'pile-concreted', 'pier', 'Pier P1 piles', 10.4, 'm³', 'Pile F1-1 bored to 13.2 m, sandstone at 11.6 m; cage lowered and concreted by tremie.', pile(1, 1, 1)],
  [57, 'pile-concreted', 'pier', 'Pier P1 piles', 10.2, 'm³', 'Pile F1-2 bored to 13.0 m and concreted.', pile(1, 1, 2)],
  [58, 'pile-concreted', 'pier', 'Pier P1 piles', 10.4, 'm³', 'Pile F1-3 bored to 13.2 m and concreted.', pile(1, 1, 3)],
  [59, 'pile-concreted', 'pier', 'Pier P1 piles', 11.3, 'm³', 'Pile F1-4: sandstone not met at design depth; bored on to 14.4 m on the Engineer’s instruction and concreted.', pile(1, 1, 4)],
  [60, 'pile-concreted', 'pier', 'Pier P1 piles', 10.3, 'm³', 'Pile F2-1 bored to 13.1 m and concreted.', pile(1, 2, 1)],
  [61, 'pile-concreted', 'pier', 'Pier P1 piles', 10.2, 'm³', 'Pile F2-2 bored to 13.0 m and concreted.', pile(1, 2, 2)],
  [62, 'pile-concreted', 'pier', 'Pier P1 piles', 10.4, 'm³', 'Pile F2-3 bored to 13.2 m and concreted.', pile(1, 2, 3)],
  [63, 'pile-concreted', 'pier', 'Pier P1 piles', 10.3, 'm³', 'Pile F2-4 bored to 13.1 m and concreted. Rig to P2.', pile(1, 2, 4)],
  [71, 'pile-test', 'pier', 'Pier P1 pile tests', null, null, 'Low-strain integrity tests on all eight P1 piles, eight days after the last; no anomaly.'],
  [72, 'rebar-fixed', 'pier', 'Pier P1 pile caps', 4.6, 't', 'P1 F-1 pile heads trimmed to cut-off level and cap reinforcement fixed.', pileCap(1, 1)],
  [74, 'formwork-erected', 'pier', 'Pier P1 pile caps', 24, 'm²', 'P1 F-1 cap forms erected.', pileCap(1, 1)],
  [74, 'rebar-fixed', 'pier', 'Pier P1 pile caps', 4.6, 't', 'P1 F-2 pile heads trimmed and cap reinforcement fixed.', pileCap(1, 2)],
  [76, 'pilecap-poured', 'pier', 'Pier P1 pile caps', 38, 'm³', 'P1 F-1 pile cap poured, Class A.', pileCap(1, 1)],
  [76, 'formwork-erected', 'pier', 'Pier P1 pile caps', 24, 'm²', 'P1 F-2 cap forms erected.', pileCap(1, 2)],
  [78, 'pilecap-poured', 'pier', 'Pier P1 pile caps', 38, 'm³', 'P1 F-2 pile cap poured.', pileCap(1, 2)],
  [83, 'concrete-cured', 'pier', 'Pier P1 pile caps', null, null, 'P1 F-1 cap cured seven days.', pileCap(1, 1)],
  [85, 'concrete-cured', 'pier', 'Pier P1 pile caps', null, null, 'P1 F-2 cap cured seven days.', pileCap(1, 2)],
  [82, 'rebar-fixed', 'pier', 'Pier P1 columns', 2.6, 't', 'P1 C-1 column cage fixed on the cap starters.', column(1, 1)],
  [84, 'formwork-erected', 'pier', 'Pier P1 columns', 34, 'm²', 'P1 C-1 steel column form erected and plumbed.', column(1, 1)],
  [86, 'concrete-poured', 'pier', 'Pier P1 columns', 11, 'm³', 'P1 C-1 column poured to the underside of the bent cap.', column(1, 1)],
  [88, 'formwork-struck', 'pier', 'Pier P1 columns', 34, 'm²', 'P1 C-1 column form struck at 48 hours; curing compound applied.', column(1, 1)],
  [84, 'rebar-fixed', 'pier', 'Pier P1 columns', 2.6, 't', 'P1 C-2 column cage fixed.', column(1, 2)],
  [86, 'formwork-erected', 'pier', 'Pier P1 columns', 34, 'm²', 'P1 C-2 column form erected.', column(1, 2)],
  [88, 'concrete-poured', 'pier', 'Pier P1 columns', 11, 'm³', 'P1 C-2 column poured.', column(1, 2)],
  [90, 'formwork-struck', 'pier', 'Pier P1 columns', 34, 'm²', 'P1 C-2 column form struck at 48 hours.', column(1, 2)],
  [91, 'backfill', 'pier', 'Pier P1 backfill', 140, 'm³', 'P1 cofferdam backfilled round the pile caps to bed level in compacted layers; sheet piles drawn.'],
  [96, 'formwork-erected', 'pier', 'Pier P1 bent cap', 92, 'm²', 'P1 bent cap falsework and soffit erected off the pile caps, eight days after the last column pour.', bentCap(1)],
  [100, 'rebar-fixed', 'pier', 'Pier P1 bent cap', 6.8, 't', 'P1 bent cap reinforcement fixed.', bentCap(1)],
  [104, 'hold-point', 'pier', 'Pier P1 bent cap', null, null, 'P1 bent cap reinforcement, cover and falsework released for concreting.', bentCap(1)],
  [105, 'concrete-poured', 'pier', 'Pier P1 bent cap', 29, 'm³', 'P1 bent cap poured, Class AA. Cylinders taken for 7 and 28 days.', bentCap(1)],
  [112, 'concrete-cured', 'pier', 'Pier P1 bent cap', null, null, 'P1 bent cap cured seven days.', bentCap(1)],
  [119, 'formwork-struck', 'pier', 'Pier P1 bent cap', 92, 'm²', 'P1 bent cap soffit and falsework struck at fourteen days.', bentCap(1)],
  [121, 'survey-check', 'bearings', 'Pier P1 bearings', null, null, 'P1 plinth levels surveyed, both faces; worst deviation 2 mm.'],
  [141, 'bearing-set', 'bearings', 'Pier P1 bearings', 10, 'nos', 'Ten bearings set on the P1 cap, five on each face.'],

  /* --- pier P2, in the channel --- */
  [57, 'excavation', 'pier', 'Pier P2 cofferdam', 130, 'm³', 'P2 cofferdam closed in the channel at the end of the causeway; excavation begun.'],
  [58, 'dewatering', 'pier', 'Pier P2 cofferdam', null, null, 'P2 cofferdam pumped down; seepage heavier than at P1, third pump brought in.'],
  [60, 'excavation', 'pier', 'Pier P2 cofferdam', 85, 'm³', 'P2 excavation at pile cap soffit.'],
  [64, 'stoppage', 'pier', 'Pier P2 cofferdam', null, null, 'The flood overtopped the P2 cofferdam as well; the causeway culverts ran full and the rig was walked back to the A1 bank.'],
  [66, 'dewatering', 'pier', 'Pier P2 cofferdam', null, null, 'P2 cofferdam pumped out and the silt cleared; the causeway made good where it scoured.'],
  [67, 'hold-point', 'pier', 'Pier P2 piles', null, null, 'Rig back on the P2 platform; pile positions rechecked after the flood.'],
  [68, 'pile-concreted', 'pier', 'Pier P2 piles', 10.9, 'm³', 'Pile F1-1 bored to 13.8 m, sandstone at 12.1 m; concreted.', pile(2, 1, 1)],
  [69, 'pile-concreted', 'pier', 'Pier P2 piles', 10.8, 'm³', 'Pile F1-2 bored to 13.7 m and concreted.', pile(2, 1, 2)],
  [70, 'pile-concreted', 'pier', 'Pier P2 piles', 11.0, 'm³', 'Pile F1-3 bored to 13.9 m and concreted.', pile(2, 1, 3)],
  [71, 'pile-concreted', 'pier', 'Pier P2 piles', 10.9, 'm³', 'Pile F1-4 bored to 13.8 m and concreted.', pile(2, 1, 4)],
  [72, 'pile-concreted', 'pier', 'Pier P2 piles', 11.1, 'm³', 'Pile F2-1 bored to 14.0 m and concreted.', pile(2, 2, 1)],
  [73, 'pile-concreted', 'pier', 'Pier P2 piles', 11.2, 'm³', 'Pile F2-2 bored to 14.1 m; casing pulled early as the tremie was raised. Concreted.', pile(2, 2, 2)],
  [74, 'pile-concreted', 'pier', 'Pier P2 piles', 10.9, 'm³', 'Pile F2-3 bored to 13.8 m and concreted.', pile(2, 2, 3)],
  [75, 'pile-concreted', 'pier', 'Pier P2 piles', 11.0, 'm³', 'Pile F2-4 bored to 13.9 m and concreted. Rig demobilised.', pile(2, 2, 4)],
  [83, 'pile-test', 'pier', 'Pier P2 pile tests', null, null, 'Low-strain integrity tests on all eight P2 piles: seven sound, a minor anomaly at 6.1 m in F2-2. Accepted by the designer.'],
  [85, 'rebar-fixed', 'pier', 'Pier P2 pile caps', 4.6, 't', 'P2 F-1 pile heads trimmed and cap reinforcement fixed.', pileCap(2, 1)],
  [87, 'formwork-erected', 'pier', 'Pier P2 pile caps', 24, 'm²', 'P2 F-1 cap forms erected.', pileCap(2, 1)],
  [87, 'rebar-fixed', 'pier', 'Pier P2 pile caps', 4.6, 't', 'P2 F-2 pile heads trimmed and cap reinforcement fixed.', pileCap(2, 2)],
  [89, 'pilecap-poured', 'pier', 'Pier P2 pile caps', 38, 'm³', 'P2 F-1 pile cap poured.', pileCap(2, 1)],
  [89, 'formwork-erected', 'pier', 'Pier P2 pile caps', 24, 'm²', 'P2 F-2 cap forms erected.', pileCap(2, 2)],
  [91, 'pilecap-poured', 'pier', 'Pier P2 pile caps', 38, 'm³', 'P2 F-2 pile cap poured.', pileCap(2, 2)],
  [96, 'concrete-cured', 'pier', 'Pier P2 pile caps', null, null, 'P2 F-1 cap cured seven days.', pileCap(2, 1)],
  [98, 'concrete-cured', 'pier', 'Pier P2 pile caps', null, null, 'P2 F-2 cap cured seven days.', pileCap(2, 2)],
  [95, 'rebar-fixed', 'pier', 'Pier P2 columns', 2.6, 't', 'P2 C-1 column cage fixed.', column(2, 1)],
  [97, 'formwork-erected', 'pier', 'Pier P2 columns', 34, 'm²', 'P2 C-1 column form erected.', column(2, 1)],
  [99, 'concrete-poured', 'pier', 'Pier P2 columns', 11, 'm³', 'P2 C-1 column poured.', column(2, 1)],
  [101, 'formwork-struck', 'pier', 'Pier P2 columns', 34, 'm²', 'P2 C-1 column form struck at 48 hours.', column(2, 1)],
  [97, 'rebar-fixed', 'pier', 'Pier P2 columns', 2.6, 't', 'P2 C-2 column cage fixed.', column(2, 2)],
  [100, 'formwork-erected', 'pier', 'Pier P2 columns', 34, 'm²', 'P2 C-2 column form erected.', column(2, 2)],
  [102, 'concrete-poured', 'pier', 'Pier P2 columns', 11, 'm³', 'P2 C-2 column poured.', column(2, 2)],
  [104, 'formwork-struck', 'pier', 'Pier P2 columns', 34, 'm²', 'P2 C-2 column form struck at 48 hours.', column(2, 2)],
  [105, 'backfill', 'pier', 'Pier P2 backfill', 150, 'm³', 'P2 cofferdam backfilled round the pile caps to bed level; sheet piles drawn.'],
  [110, 'formwork-erected', 'pier', 'Pier P2 bent cap', 92, 'm²', 'P2 bent cap falsework and soffit erected.', bentCap(2)],
  [113, 'rebar-fixed', 'pier', 'Pier P2 bent cap', 6.8, 't', 'P2 bent cap reinforcement fixed.', bentCap(2)],
  [118, 'hold-point', 'pier', 'Pier P2 bent cap', null, null, 'P2 bent cap released for concreting.', bentCap(2)],
  [119, 'concrete-poured', 'pier', 'Pier P2 bent cap', 29, 'm³', 'P2 bent cap poured, Class AA. Cylinders taken for 7 and 28 days.', bentCap(2)],
  [126, 'concrete-cured', 'pier', 'Pier P2 bent cap', null, null, 'P2 bent cap cured seven days.', bentCap(2)],
  [133, 'formwork-struck', 'pier', 'Pier P2 bent cap', 92, 'm²', 'P2 bent cap soffit and falsework struck at fourteen days.', bentCap(2)],
  [135, 'survey-check', 'bearings', 'Pier P2 bearings', null, null, 'P2 plinth levels surveyed, both faces; worst deviation 3 mm.'],
  [143, 'bearing-set', 'bearings', 'Pier P2 bearings', 10, 'nos', 'Ten bearings set on the P2 cap. Every bearing on the bridge now set.'],

  /* --- the girders, in the yard: two moulds, a pair every six days --- */
  [44, 'girder-stressed', null, 'Span 1 girders (yard)', 2, 'nos', 'Eighteen 15.2 mm strands stressed on the bed for span 1 girders G1 and G2.'],
  [44, 'girder-cast', null, 'Span 1 girders (yard)', 2, 'nos', 'Span 1 G1 and G2 cast on the stressed strands, Class P.'],
  [49, 'hold-point', null, 'Span 1 girders (yard)', null, null, 'Span 1 G1 and G2 at five days, cylinders 31.2 MPa against 28.0 for transfer; strands released and cut.'],
  [50, 'girder-stressed', null, 'Span 1 girders (yard)', 2, 'nos', 'Strands stressed for span 1 G3 and G4.'],
  [50, 'girder-cast', null, 'Span 1 girders (yard)', 2, 'nos', 'Span 1 G3 and G4 cast.'],
  [55, 'hold-point', null, 'Span 1 girders (yard)', null, null, 'Span 1 G3 and G4 detensioned at transfer strength.'],
  [56, 'girder-stressed', null, 'Span 1 girders (yard)', 1, 'nos', 'Strands stressed for span 1 G5 in one mould.'],
  [56, 'girder-cast', null, 'Span 1 girders (yard)', 1, 'nos', 'Span 1 G5 cast.'],
  [56, 'girder-stressed', null, 'Span 2 girders (yard)', 1, 'nos', 'Strands stressed for span 2 G1 in the other.'],
  [56, 'girder-cast', null, 'Span 2 girders (yard)', 1, 'nos', 'Span 2 G1 cast.'],
  [61, 'hold-point', null, 'Span 1 girders (yard)', null, null, 'Span 1 G5 detensioned. All five span 1 girders stacked in the yard.'],
  [61, 'hold-point', null, 'Span 2 girders (yard)', null, null, 'Span 2 G1 detensioned.'],
  [62, 'girder-stressed', null, 'Span 2 girders (yard)', 2, 'nos', 'Strands stressed for span 2 G2 and G3.'],
  [62, 'girder-cast', null, 'Span 2 girders (yard)', 2, 'nos', 'Span 2 G2 and G3 cast.'],
  [67, 'hold-point', null, 'Span 2 girders (yard)', null, null, 'Span 2 G2 and G3 detensioned at transfer strength.'],
  [68, 'girder-stressed', null, 'Span 2 girders (yard)', 2, 'nos', 'Strands stressed for span 2 G4 and G5.'],
  [68, 'girder-cast', null, 'Span 2 girders (yard)', 2, 'nos', 'Span 2 G4 and G5 cast.'],
  [73, 'hold-point', null, 'Span 2 girders (yard)', null, null, 'Span 2 G4 and G5 detensioned. All five span 2 girders stacked.'],
  [74, 'girder-stressed', null, 'Span 3 girders (yard)', 2, 'nos', 'Strands stressed for span 3 G1 and G2.'],
  [74, 'girder-cast', null, 'Span 3 girders (yard)', 2, 'nos', 'Span 3 G1 and G2 cast.'],
  [79, 'hold-point', null, 'Span 3 girders (yard)', null, null, 'Span 3 G1 and G2 detensioned at transfer strength.'],
  [80, 'girder-stressed', null, 'Span 3 girders (yard)', 2, 'nos', 'Strands stressed for span 3 G3 and G4.'],
  [80, 'girder-cast', null, 'Span 3 girders (yard)', 2, 'nos', 'Span 3 G3 and G4 cast.'],
  [85, 'hold-point', null, 'Span 3 girders (yard)', null, null, 'Span 3 G3 and G4 detensioned at transfer strength.'],
  [86, 'girder-stressed', null, 'Span 3 girders (yard)', 1, 'nos', 'Strands stressed for span 3 G5, the last of fifteen.'],
  [86, 'girder-cast', null, 'Span 3 girders (yard)', 1, 'nos', 'Span 3 G5 cast.'],
  [91, 'hold-point', null, 'Span 3 girders (yard)', null, null, 'Span 3 G5 detensioned. All fifteen girders stacked in the yard.'],

  /* --- on at last --- */
  [144, 'hold-point', 'deck', 'Span 1 girders', null, null, 'HP-001: 220 t crawler crane rigged on the A1 bank; lift plan and ground bearing accepted.'],
  [145, 'girder-erected', 'deck', 'Span 1 girders', 2, 'nos', 'Span 1 G5 and G4 landed on the A1 and P1 bearings.'],
  [146, 'girder-erected', 'deck', 'Span 1 girders', 1, 'nos', 'Span 1 G3 landed.'],
  [147, 'stoppage', 'deck', 'Span 1 girders', null, null, 'Squalls from 10:00, gusts over the crane limit; boom lowered.'],
  [148, 'girder-erected', 'deck', 'Span 1 girders', 1, 'nos', 'Span 1 G2 landed.'],
  [149, 'girder-erected', 'deck', 'Span 1 girders', 1, 'nos', 'Span 1 G1 landed. All five span 1 girders on; crane to walk out onto the causeway for span 2.'],
  [150, 'rebar-fixed', 'deck', 'Span 1 diaphragms', 3.8, 't', 'End and midspan diaphragm reinforcement fixed between the span 1 girders.'],
];

/** Every Adlayan entry, before sliding, oldest first. */
function adlayanWorks(): ProgressEntry[] {
  const pool = new Map(Object.entries(PHOTO_POOL).map(([k, v]) => [k, [...v]]));
  const girderIds: Record<number, number[]> = { 145: [5, 4], 146: [3], 148: [2], 149: [1] };
  return ADLAYAN_ROWS.map((row, order) => ({ row, order }))
    .sort((a, b) => a.row[0] - b.row[0] || a.order - b.order)
    .map(({ row: [n, activity, element, component, quantity, unit, note, componentId] }, i) => {
      const id = `${WORKS_PREFIX}adl-${String(i + 1).padStart(3, '0')}`;
      const occurredOn = day(n);
      const created = `${occurredOn}T${String(9 + (i % 6)).padStart(2, '0')}:15:00Z`;
      const state: ProgressEntry['state'] = LAST_DAY - n < 4 ? 'draft' : LAST_DAY - n < 9 ? 'review' : 'accepted';
      const reviewed = state === 'accepted' ? `${addDays(occurredOn, 1)}T01:30:00Z` : null;
      const shot = pool.get(activity)?.shift();
      const photo = shot === undefined ? null : `./demo/progress/atk-${String(shot).padStart(3, '0')}-${activity}`;
      const lifted = activity === 'girder-erected' ? girderIds[n] : undefined;
      return {
        id,
        clientId: id,
        bridgeId: ADLAYAN_ID,
        activity,
        element,
        component,
        componentId: componentId ?? (lifted ? girder(lifted[0]!) : null),
        quantity,
        unit,
        note,
        occurredOn,
        author: PEOPLE.site,
        state,
        reviewedBy: reviewed === null ? null : PEOPLE.resident,
        reviewedAt: reviewed,
        hasPhoto: photo !== null,
        ...(photo ? { photoUrl: `${photo}.jpg`, photoThumbUrl: `${photo}-t.jpg` } : {}),
        createdAt: created,
        updatedAt: reviewed ?? created,
      };
    });
}

function slide(e: ProgressEntry, days: number): ProgressEntry {
  return {
    ...e,
    occurredOn: addDays(e.occurredOn, days),
    createdAt: addDays(e.createdAt, days),
    updatedAt: addDays(e.updatedAt, days),
    reviewedAt: e.reviewedAt === null ? null : addDays(e.reviewedAt, days),
  };
}

/* --------------------------- daily returns --------------------------- */

/**
 * The weather at the site, Agusan del Sur from late April: no dry season to
 * speak of, afternoon thunderstorms, and two days of the creek in flood.
 * Days not listed were clear.
 */
const WEATHER: Readonly<Record<number, string>> = {
  3: 'rain', 7: 'overcast', 11: 'rain', 12: 'overcast', 18: 'rain', 21: 'overcast', 26: 'rain', 27: 'rain',
  31: 'overcast', 37: 'rain', 40: 'overcast', 44: 'storm', 45: 'rain', 52: 'overcast', 57: 'rain',
  63: 'rain', 64: 'heavy-rain', 65: 'heavy-rain', 66: 'overcast', 72: 'rain', 75: 'overcast', 79: 'rain',
  83: 'rain', 86: 'overcast', 93: 'rain', 95: 'rain', 100: 'overcast', 105: 'rain', 107: 'rain',
  115: 'overcast', 117: 'rain', 118: 'heavy-rain', 120: 'overcast', 125: 'rain', 131: 'rain',
  133: 'overcast', 136: 'rain', 139: 'overcast', 143: 'rain', 147: 'storm',
};

const RAIN_MM: Readonly<Record<string, number>> = { clear: 0, overcast: 0, rain: 14, 'heavy-rain': 78, storm: 46 };

function gangFor(activities: readonly string[], n: number): { trades: { trade: string; count: number }[]; plant: string[] } {
  const has = (...ids: string[]) => activities.some((a) => ids.includes(a));
  const trades: { trade: string; count: number }[] = [{ trade: 'General labour', count: n < 14 ? 10 : 18 }];
  const plant: string[] = ['Generator'];
  if (has('excavation', 'dewatering', 'backfill')) {
    trades.push({ trade: 'Operators', count: 3 });
    plant.push('Excavator', 'Dump truck ×3', 'Pumps');
  }
  if (has('pile-concreted', 'pile-test')) {
    trades.push({ trade: 'Piling crew', count: 6 });
    plant.push('Piling rig', 'Service crane 50 t');
  }
  if (has('rebar-fixed')) trades.push({ trade: 'Steel fixers', count: 10 });
  if (has('formwork-erected', 'formwork-struck')) trades.push({ trade: 'Carpenters', count: 8 });
  if (has('concrete-poured', 'pilecap-poured', 'footing-poured')) {
    trades.push({ trade: 'Concrete gang', count: 12 });
    plant.push('Concrete pump', 'Transit mixers ×4');
  }
  if (has('girder-erected') || (n >= 144 && n <= 149)) {
    trades.push({ trade: 'Riggers', count: 8 });
    plant.push('Crawler crane 220 t');
  }
  if (has('bearing-set')) trades.push({ trade: 'Fitters', count: 4 });
  if (n >= 34 && n <= 91) {
    trades.push({ trade: 'Casting yard', count: 12 });
    plant.push('Yard gantry', has('girder-stressed') ? 'Stressing jack' : 'Yard vibrators');
  }
  if (has('setting-out', 'survey-check')) {
    trades.push({ trade: 'Surveyors', count: 2 });
    plant.push('Total station');
  }
  return { trades, plant };
}

function adlayanReturns(works: readonly ProgressEntry[]): SiteReturn[] {
  return Array.from({ length: LAST_DAY + 1 }, (_, n) => {
    const on = day(n);
    const id = `${SITE_PREFIX}ret-adl-${String(n + 1).padStart(3, '0')}`;
    const weather = WEATHER[n] ?? 'clear';
    const done = works.filter((e) => e.occurredOn === on);
    const stopped = done.some((e) => e.activity === 'stoppage');
    const lost = weather === 'heavy-rain';
    const gang = gangFor(done.map((e) => e.activity), n);
    const trades = lost ? [{ trade: 'Watchmen', count: 3 }, { trade: 'Pump attendants', count: 4 }] : gang.trades;
    const labour = trades.reduce((sum, t) => sum + t.count, 0);
    const plant = lost ? ['Pumps', 'Generator'] : gang.plant;
    const hours = lost ? 4 : stopped || weather === 'storm' ? 6 : 9;
    const state: SiteReturn['state'] = LAST_DAY - n < 4 ? 'draft' : LAST_DAY - n < 9 ? 'review' : 'accepted';
    const reviewed = state === 'accepted' ? `${addDays(on, 1)}T01:45:00Z` : null;
    const note = lost
      ? 'Creek in flood; site closed except the pumps.'
      : stopped
        ? 'Work stopped for part of the day — see the works entry.'
        : weather === 'rain'
          ? 'Afternoon showers; pours finished before noon.'
          : '';
    return {
      id,
      clientId: id,
      bridgeId: ADLAYAN_ID,
      returnedOn: on,
      weather,
      rainfallMm: RAIN_MM[weather] ?? 0,
      labour: stopped && !lost ? Math.round(labour * 0.7) : labour,
      labourNote: trades.map((t) => `${t.trade.toLowerCase()} ${t.count}`).join(', '),
      labourByTrade: trades,
      plant: plant.join(', '),
      plantRows: plant.map((p) => ({ plant: p, workingHours: lost || stopped ? 2 : hours - 1, idleHours: lost || stopped ? hours - 2 : 1 })),
      hours,
      note,
      author: PEOPLE.site,
      state,
      reviewedBy: reviewed === null ? null : PEOPLE.resident,
      reviewedAt: reviewed,
      createdAt: `${on}T09:30:00Z`,
      updatedAt: reviewed ?? `${on}T09:30:00Z`,
    };
  });
}

/* --------------------------- incidents --------------------------- */

function adlayanIncidents(): SiteIncident[] {
  const rows: (Pick<SiteIncident, 'kind' | 'element' | 'description' | 'action'> &
    Partial<SiteIncident> & { n: number; closedN: number | null })[] = [
    {
      n: 13, closedN: 15, kind: 'environmental', element: null,
      description: 'Silty runoff from the detour embankment reached the creek after an evening shower.',
      action: 'Silt curtain across the outfall, the embankment face covered with coco-net, and a sump at the toe.',
    },
    {
      n: 64, closedN: 67, kind: 'dangerous-occurrence', element: 'abutment', daysLost: 2,
      description: 'The creek overtopped the A2 and P2 cofferdams overnight and flooded both excavations. Plant withdrawn at 04:00 and the rig walked off the causeway; nobody in either excavation.',
      action: 'Creek gauge read at 18:00 and 04:00; work at A2 and on the causeway stops at a rise of 0.5 m in six hours.',
    },
    {
      n: 73, closedN: 74, kind: 'near-miss', element: 'pier',
      description: 'A temporary casing slipped in the rig’s clamp at P2 and dropped 0.5 m into the bore; the banksman was clear.',
      action: 'Clamp jaws replaced, and an exclusion zone marked round the casing on the platform.',
    },
    {
      n: 96, closedN: 96, kind: 'first-aid', element: 'abutment', peopleInvolved: 1,
      description: 'Carpenter cut his hand on a form tie at the A1 wing walls.',
      action: 'Dressed on site. Tie ends capped and cut-resistant gloves issued to the form gang.',
    },
    {
      n: 146, closedN: 147, kind: 'near-miss', element: 'deck',
      description: 'Girder G3 swung on the lift when a tag line was let go; it passed within 2 m of the bearing crew at P1.',
      action: 'Two tag lines per girder, and nobody under a lift until the banksman clears it.',
    },
    {
      n: 149, closedN: null, kind: 'near-miss', element: 'deck',
      description: 'Steel fixer found on the girder top flanges fixing diaphragm bars without a harness.',
      action: 'Static line along the girders ordered before diaphragm work continues.',
    },
  ];
  return rows.map(({ n, closedN, ...r }, i) => {
    const id = `${SITE_PREFIX}inc-adl-${String(i + 1).padStart(3, '0')}`;
    const occurredOn = day(n);
    return {
      ...r,
      id,
      clientId: id,
      bridgeId: ADLAYAN_ID,
      occurredOn,
      closed: closedN !== null,
      closedOn: closedN === null ? null : day(closedN),
      closedBy: closedN === null ? null : PEOPLE.resident,
      author: PEOPLE.site,
      createdAt: `${occurredOn}T08:10:00Z`,
      updatedAt: `${closedN === null ? occurredOn : day(closedN)}T08:10:00Z`,
    };
  });
}

/* --------------------------- documents --------------------------- */

const DOCUMENTS: readonly { kind: string; title: string; n: number; work?: [string, number]; note: string }[] = [
  { kind: 'method-statement', title: 'ADL-MS-001 Temporary detour and traffic management', n: -5, note: 'Detour alignment, the steel panel bridge, signing and flagmen.' },
  { kind: 'method-statement', title: 'ADL-MS-003 Abutment excavation and spread footings at A1 and A2', n: 40, note: 'Cofferdams, dewatering, formation inspection and the creek-level trigger.' },
  { kind: 'material-certificate', title: 'Prestressing strand mill certificate — 15.2 mm Grade 270, lot PH-26-0418', n: 42, note: '' },
  { kind: 'other', title: 'Formation inspection record — A1', n: 57, work: ['hold-point', 57], note: 'Weathered sandstone across the footprint.' },
  { kind: 'ncr', title: 'NCR-001 Soft pocket under the A2 footing', n: 67, work: ['non-conformance', 67], note: 'About 3 m by 4 m at the upstream end.' },
  { kind: 'other', title: 'Designer’s instruction — A2 formation', n: 68, note: 'Dig out to sandstone and fill with lean concrete, Class C.' },
  { kind: 'ncr', title: 'NCR-002 Honeycombing at the A1 front wall base', n: 82, work: ['non-conformance', 82], note: '' },
  { kind: 'other', title: 'Formation inspection record — A2, after the fill', n: 86, work: ['hold-point', 86], note: 'Fill probed at 14 days; formation accepted.' },
  { kind: 'test-certificate', title: '28-day cylinder results — A1 footing', n: 98, work: ['footing-poured', 70], note: '27.6 MPa mean against 20.7 MPa specified.' },
  { kind: 'other', title: 'Bearing supplier letter — revised delivery date', n: 104, note: 'Elastomeric bearings rescheduled from day 108 to day 139.' },
  { kind: 'method-statement', title: 'ADL-MS-007 Girder erection by 220 t crawler crane', n: 138, note: 'Lift plan for all three spans, ground bearing on the A1 bank, the causeway and the A2 bank, and the exclusion zone.' },
  { kind: 'method-statement', title: 'ADL-MS-004 Causeway, cofferdams and bored piles at P1 and P2', n: 44, note: 'Rock causeway, sheet-pile cofferdams, cased bores and tremie concrete; the creek-level trigger.' },
  { kind: 'test-certificate', title: 'Pile integrity test report — P1, piles F1-1 to F2-4', n: 71, work: ['pile-test', 71], note: 'Low-strain; all eight sound.' },
  { kind: 'test-certificate', title: 'Pile integrity test report — P2, piles F1-1 to F2-4', n: 83, work: ['pile-test', 83], note: 'Low-strain; seven sound, F2-2 a minor anomaly at 6.1 m.' },
];

function adlayanDocuments(slidWorks: readonly ProgressEntry[]): SiteDocument[] {
  const workOn = (activity: string, n: number): string | null =>
    slidWorks.find((e) => e.activity === activity && e.occurredOn === slidDay(n))?.id ?? null;
  return DOCUMENTS.map((r, i) => {
    const id = `${SITE_PREFIX}doc-adl-${String(i + 1).padStart(3, '0')}`;
    const text = [
      'DEMONSTRATION DOCUMENT — IT CERTIFIES NOTHING.',
      r.title,
      `Adlayan Bridge replacement, ${ADLAYAN_ID}, DMRP package A3`,
      '',
    ].join('\n');
    const datedOn = day(r.n);
    return {
      id,
      clientId: id,
      bridgeId: ADLAYAN_ID,
      eventId: r.work ? workOn(r.work[0], r.work[1]) : null,
      kind: r.kind,
      title: r.title,
      datedOn,
      note: r.note,
      contentType: 'text/plain',
      bytes: new TextEncoder().encode(text).length,
      filename: `${r.title.replace(/[^\w\- ]+/g, '').replace(/\s+/g, ' ').trim()}.txt`,
      fileUrl: `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`,
      author: PEOPLE.site,
      createdAt: `${datedOn}T05:00:00Z`,
    };
  });
}

/* --------------------------- tests --------------------------- */

/** The test whose failure raised each NCR, and the NCR's number. */
const NCR_OF_TEST: Readonly<Record<number, number>> = { 7: 3 };

function adlayanTests(slidWorks: readonly ProgressEntry[]): SiteTest[] {
  const workOn = (activity: string, n: number, component: RegExp): string | null =>
    slidWorks.find((e) => e.activity === activity && e.occurredOn === slidDay(n) && component.test(e.component ?? ''))?.id ?? null;
  type Row = Partial<SiteTest> & { kind: string; n: number; pour?: [string, number, RegExp]; complies?: boolean };
  const rows: Row[] = [
    { kind: 'strand-cert', n: 42, grade: 'Grade 270, 15.2 mm (140 mm²)', lot: 'PH-26-0418', complies: true, documentId: `${SITE_PREFIX}doc-adl-003` },
    { kind: 'concrete-cylinder', n: 49, pour: ['girder-cast', 44, /yard/], component: 'Span 1 girders (yard)', mix: 'Class P', ageDays: 5, strengthMpa: 31.2, requiredMpa: 28.0, note: 'Transfer strength.' },
    { kind: 'concrete-cylinder', n: 72, pour: ['girder-cast', 44, /yard/], component: 'Span 1 girders (yard)', mix: 'Class P', ageDays: 28, strengthMpa: 41.8, requiredMpa: 37.9 },
    { kind: 'concrete-cylinder', n: 77, pour: ['footing-poured', 70, /A1 footing/], component: 'Abutment A1 footing', componentId: PART.a1Footing, mix: 'Class A', ageDays: 7, strengthMpa: 17.9, requiredMpa: 14.5 },
    { kind: 'concrete-cylinder', n: 98, pour: ['footing-poured', 70, /A1 footing/], component: 'Abutment A1 footing', componentId: PART.a1Footing, mix: 'Class A', ageDays: 28, strengthMpa: 27.6, requiredMpa: 20.7, documentId: `${SITE_PREFIX}doc-adl-009` },
    { kind: 'concrete-cylinder', n: 108, pour: ['concrete-poured', 80, /A1 front wall/], component: 'Abutment A1 front wall', componentId: PART.a1FrontWall, mix: 'Class A', ageDays: 28, strengthMpa: 25.9, requiredMpa: 20.7 },
    { kind: 'concrete-cylinder', n: 119, pour: ['footing-poured', 91, /A2 footing/], component: 'Abutment A2 footing', componentId: PART.a2Footing, mix: 'Class A', ageDays: 28, strengthMpa: 26.4, requiredMpa: 20.7 },
    { kind: 'concrete-cylinder', n: 140, pour: ['concrete-poured', 112, /A2 wing walls/], component: 'Abutment A2 wing walls', mix: 'Class A', ageDays: 28, strengthMpa: 19.4, requiredMpa: 20.7, note: 'Three cylinders, 18.8 to 20.1 MPa. Cores ordered.' },
    { kind: 'concrete-cylinder', n: 129, pour: ['concrete-poured', 101, /A2 front wall/], component: 'Abutment A2 front wall', componentId: PART.a2FrontWall, mix: 'Class A', ageDays: 28, strengthMpa: 24.8, requiredMpa: 20.7 },
    { kind: 'pile-integrity', n: 71, pour: ['pile-test', 71, /P1/], component: 'Pier P1 pile tests', method: 'low-strain', resultClass: 'sound', documentId: `${SITE_PREFIX}doc-adl-013` },
    { kind: 'pile-integrity', n: 83, pour: ['pile-test', 83, /P2/], component: 'Pier P2 pile tests', method: 'low-strain', resultClass: 'minor-anomaly', note: 'F2-2: impedance change at 6.1 m; the other seven sound. Accepted by the designer.', documentId: `${SITE_PREFIX}doc-adl-014` },
    { kind: 'concrete-cylinder', n: 104, pour: ['pilecap-poured', 76, /P1 pile caps/], component: 'Pier P1 pile caps', componentId: pileCap(1, 1), mix: 'Class A', ageDays: 28, strengthMpa: 26.1, requiredMpa: 20.7 },
    { kind: 'concrete-cylinder', n: 112, pour: ['concrete-poured', 105, /P1 bent cap/], component: 'Pier P1 bent cap', componentId: bentCap(1), mix: 'Class AA', ageDays: 7, strengthMpa: 21.8, requiredMpa: 18.6 },
    { kind: 'concrete-cylinder', n: 133, pour: ['concrete-poured', 105, /P1 bent cap/], component: 'Pier P1 bent cap', componentId: bentCap(1), mix: 'Class AA', ageDays: 28, strengthMpa: 31.4, requiredMpa: 27.6 },
    { kind: 'concrete-cylinder', n: 117, pour: ['pilecap-poured', 89, /P2 pile caps/], component: 'Pier P2 pile caps', componentId: pileCap(2, 1), mix: 'Class A', ageDays: 28, strengthMpa: 25.3, requiredMpa: 20.7 },
    { kind: 'concrete-cylinder', n: 147, pour: ['concrete-poured', 119, /P2 bent cap/], component: 'Pier P2 bent cap', componentId: bentCap(2), mix: 'Class AA', ageDays: 28, strengthMpa: 30.2, requiredMpa: 27.6 },
  ];
  return rows.map(({ n, pour, complies, ...r }, i) => {
    const id = `${SITE_PREFIX}test-adl-${String(i + 1).padStart(3, '0')}`;
    const testedOn = day(n);
    const test: SiteTest = {
      id,
      clientId: id,
      bridgeId: ADLAYAN_ID,
      testedOn,
      eventId: pour ? workOn(...pour) : null,
      component: null,
      componentId: null,
      documentId: null,
      mix: null,
      ageDays: null,
      strengthMpa: null,
      requiredMpa: null,
      method: null,
      resultClass: null,
      testLoadKn: null,
      settlementMm: null,
      allowedMm: null,
      lot: null,
      grade: null,
      ncrId: null,
      ncrRef: null,
      note: '',
      author: PEOPLE.site,
      createdAt: `${testedOn}T06:00:00Z`,
      ...r,
      passed: false,
    };
    test.passed = testPasses({ ...test, complies: complies ?? null }) === true;
    const ncr = NCR_OF_TEST[i];
    if (!test.passed && ncr !== undefined) {
      test.ncrId = `${SITE_PREFIX}item-adl-ncr-${String(ncr).padStart(3, '0')}`;
      test.ncrRef = itemRef('ncr', ncr);
    }
    return test;
  });
}

/* --------------------------- the register of items --------------------------- */

function adlayanItems(tests: readonly SiteTest[]): SiteItem[] {
  const at = (n: number, time: string) => `${day(n)}T${time}:00Z`;
  const cylinders = tests[7]!;
  const item = (over: Partial<SiteItem> & Pick<SiteItem, 'id' | 'kind' | 'seq' | 'title' | 'status' | 'raisedBy' | 'raisedAt'>): SiteItem => ({
    clientId: over.id,
    bridgeId: ADLAYAN_ID,
    ref: itemRef(over.kind as 'ncr' | 'rfi' | 'hold-point', over.seq),
    detail: '',
    owner: PEOPLE.resident,
    dueOn: null,
    activityId: null,
    activityName: null,
    component: null,
    componentId: null,
    eventId: null,
    testId: null,
    answer: null,
    answeredBy: null,
    answeredAt: null,
    closeNote: null,
    closedBy: null,
    closedAt: null,
    updatedAt: over.closedAt ?? over.answeredAt ?? over.raisedAt,
    ...over,
  });
  return [
    item({
      id: `${SITE_PREFIX}item-adl-ncr-001`, kind: 'ncr', seq: 1, status: 'closed',
      title: 'Soft pocket under the A2 footing',
      detail: 'A pocket of soft clay about 3 m by 4 m at formation level under the upstream end of the A2 footprint. Dig out and replace on the designer’s instruction.',
      component: 'Abutment A2 formation', componentId: PART.a2Footing,
      raisedBy: PEOPLE.resident, raisedAt: at(67, '06:00'),
      closeNote: 'Dug out to sandstone and filled with lean concrete; probed and accepted by the designer.', closedBy: PEOPLE.resident, closedAt: at(86, '10:00'),
    }),
    item({
      id: `${SITE_PREFIX}item-adl-ncr-002`, kind: 'ncr', seq: 2, status: 'closed',
      title: 'Honeycombing at the A1 front wall base',
      detail: 'Honeycombing over 0.6 m² where the kicker leaked. Repair by approved polymer mortar.',
      component: 'Abutment A1 front wall', componentId: PART.a1FrontWall,
      raisedBy: PEOPLE.resident, raisedAt: at(82, '07:00'),
      closeNote: 'Repair inspected and accepted.', closedBy: PEOPLE.resident, closedAt: at(85, '03:00'),
    }),
    item({
      id: cylinders.ncrId!, kind: 'ncr', seq: 3, status: 'open',
      title: failedTestTitle(cylinders),
      detail: 'Cores to be taken from both A2 wing walls and tested before the backfill is placed.',
      dueOn: day(156),
      component: cylinders.component, testId: cylinders.id, eventId: cylinders.eventId,
      raisedBy: cylinders.author, raisedAt: cylinders.createdAt,
    }),
    item({
      id: `${SITE_PREFIX}item-adl-hp-001`, kind: 'hold-point', seq: 1, status: 'closed',
      title: 'Girder erection, span 1',
      detail: 'Bearings set at A1 and P1, crane rigged on the A1 bank, lift plan ADL-MS-007 accepted.',
      activityId: 'A1620', activityName: 'Span 1 girders — erect', component: 'Span 1 girders',
      dueOn: day(144),
      raisedBy: PEOPLE.site, raisedAt: at(141, '08:00'),
      closeNote: 'Released for the lift. P1 bent cap 39 days old, 28-day cylinders 31.4 MPa.', closedBy: PEOPLE.resident, closedAt: at(144, '06:30'),
    }),
    item({
      id: `${SITE_PREFIX}item-adl-rfi-001`, kind: 'rfi', seq: 1, status: 'closed',
      title: 'Substitute bearing supplier',
      detail: 'The nominated supplier cannot deliver before day 139. A local supplier offers day 118 to the same specification.',
      component: 'Abutment A1 bearings',
      raisedBy: PEOPLE.site, raisedAt: at(104, '02:00'),
      answer: 'Not approved — the substitute has no type test to the DPWH specification. Keep the nominated supplier.', answeredBy: PEOPLE.resident, answeredAt: at(106, '03:00'),
      closeNote: 'Closed on the answer.', closedBy: PEOPLE.resident, closedAt: at(107, '01:00'),
    }),
    item({
      id: `${SITE_PREFIX}item-adl-rfi-002`, kind: 'rfi', seq: 2, status: 'answered',
      title: 'Expansion joint recess at the A1 back wall',
      detail: 'Drawing ADL-S-112 shows a 75 mm recess; the strip-seal supplier asks for 90 mm.',
      component: 'Abutment A1 back wall',
      raisedBy: PEOPLE.site, raisedAt: at(136, '02:00'),
      answer: 'Form the recess to 90 mm. ADL-S-112 Rev B issued.', answeredBy: PEOPLE.resident, answeredAt: at(139, '03:00'),
    }),
  ];
}

/* --------------------------- the programme --------------------------- */

type Leaf = [id: string, parent: string, name: string, kind: string, days: number, logic: string, component: string, doneOn: number | null];
type ProgrammeRow = [id: string, parent: string | null, name: string] | Leaf;
const isLeaf = (r: ProgrammeRow): r is Leaf => r.length > 3;

/**
 * The contract programme, frozen before work began: durations and logic, solved
 * by src/data/programme.ts on a seven-day calendar. Same notation as the Indus
 * Relief Bridge's — `A1210+6` a lag, `:SS` start-to-start, `@180` not before.
 */
const PROGRAMME: readonly ProgrammeRow[] = [
  ['A1000', null, 'Adlayan Bridge replacement'],
  ['A1100', 'A1000', 'Preliminaries'],
  ['A1110', 'A1100', 'Mobilisation and site establishment', 'Preliminaries', 14, '', 'Site establishment', 14],
  ['A1120', 'A1100', 'Setting out', 'Survey', 3, 'A1110:SS+4', 'All supports', 6],
  ['A1130', 'A1100', 'Temporary detour and traffic switch', 'Temporary Works', 24, 'A1110:SS+8', 'Temporary detour', 33],
  ['A1140', 'A1100', 'Casting yard and moulds', 'Preliminaries', 24, 'A1110:SS+8', 'Casting yard', 34],
  ['A1150', 'A1100', 'Removal of the old bridge', 'Demolition', 12, 'A1130', 'Old bridge removal', 47],
  ['A1160', 'A1100', 'Causeway and piling platforms', 'Temporary Works', 4, 'A1150', 'Temporary causeway', 51],

  ['A1200', 'A1000', 'Abutment A1'],
  ['A1210', 'A1200', 'Excavation to founding level', 'Excavation', 14, 'A1150', 'Abutment A1 excavation', 55],
  ['A1230', 'A1200', 'Formation inspection', 'Inspection', 2, 'A1210', 'Abutment A1 formation', 57],
  ['A1240', 'A1200', 'Footing', 'Footing', 9, 'A1230', 'Abutment A1 footing', 70],
  ['A1250', 'A1200', 'Front wall', 'Wall', 10, 'A1240+3', 'Abutment A1 front wall', 85],
  ['A1260', 'A1200', 'Bearing beam', 'Wall', 6, 'A1250', 'Abutment A1 bearing beam', 92],
  ['A1270', 'A1200', 'Wing walls', 'Wing Wall', 9, 'A1250:SS+10', 'Abutment A1 wing walls', 97],
  ['A1280', 'A1200', 'Bearings', 'Bearing', 2, 'A1260+14', 'Abutment A1 bearings', 140],
  ['A1290', 'A1200', 'Back wall', 'Back Wall', 8, 'A1620+3', 'Abutment A1 back wall', null],
  ['A1295', 'A1200', 'Backfill and approach slab', 'Approach Slab', 12, 'A1290+7', 'Abutment A1 backfill', null],

  ['A1400', 'A1000', 'Pier P1'],
  ['A1410', 'A1400', 'Cofferdam and excavation', 'Excavation', 4, 'A1160', 'Pier P1 cofferdam', 54],
  ['A1420', 'A1400', 'Bored piles', 'Pile', 10, 'A1410', 'Pier P1 piles', 63],
  ['A1425', 'A1400', 'Pile integrity tests', 'Test', 1, 'A1420+7', 'Pier P1 pile tests', 71],
  ['A1430', 'A1400', 'Pile caps', 'Pile Cap', 7, 'A1425', 'Pier P1 pile caps', 78],
  ['A1440', 'A1400', 'Columns', 'Column', 8, 'A1430+3', 'Pier P1 columns', 88],
  ['A1445', 'A1400', 'Backfill round the pile caps', 'Backfill', 3, 'A1440', 'Pier P1 backfill', 91],
  ['A1450', 'A1400', 'Bent cap', 'Bent Cap', 12, 'A1440+7', 'Pier P1 bent cap', 105],
  ['A1460', 'A1400', 'Bearings', 'Bearing', 2, 'A1450+21', 'Pier P1 bearings', 141],

  ['A1500', 'A1000', 'Pier P2'],
  ['A1510', 'A1500', 'Cofferdam and excavation', 'Excavation', 4, 'A1160 A1410:SS+5', 'Pier P2 cofferdam', 60],
  ['A1520', 'A1500', 'Bored piles', 'Pile', 10, 'A1420 A1510', 'Pier P2 piles', 75],
  ['A1525', 'A1500', 'Pile integrity tests', 'Test', 1, 'A1520+7', 'Pier P2 pile tests', 83],
  ['A1530', 'A1500', 'Pile caps', 'Pile Cap', 7, 'A1525', 'Pier P2 pile caps', 91],
  ['A1540', 'A1500', 'Columns', 'Column', 8, 'A1530+3', 'Pier P2 columns', 102],
  ['A1545', 'A1500', 'Backfill round the pile caps', 'Backfill', 3, 'A1540', 'Pier P2 backfill', 105],
  ['A1550', 'A1500', 'Bent cap', 'Bent Cap', 12, 'A1540+7', 'Pier P2 bent cap', 119],
  ['A1560', 'A1500', 'Bearings', 'Bearing', 2, 'A1550+21', 'Pier P2 bearings', 143],

  ['A1300', 'A1000', 'Abutment A2'],
  ['A1310', 'A1300', 'Excavation to founding level', 'Excavation', 14, 'A1150 A1210:SS+8', 'Abutment A2 excavation', 71],
  ['A1330', 'A1300', 'Formation inspection', 'Inspection', 2, 'A1310', 'Abutment A2 formation', 86],
  ['A1340', 'A1300', 'Footing', 'Footing', 9, 'A1330', 'Abutment A2 footing', 91],
  ['A1350', 'A1300', 'Front wall', 'Wall', 10, 'A1340+3', 'Abutment A2 front wall', 103],
  ['A1360', 'A1300', 'Bearing beam', 'Wall', 6, 'A1350', 'Abutment A2 bearing beam', 114],
  ['A1370', 'A1300', 'Wing walls', 'Wing Wall', 9, 'A1350:SS+10', 'Abutment A2 wing walls', 112],
  ['A1380', 'A1300', 'Bearings', 'Bearing', 2, 'A1360+14', 'Abutment A2 bearings', 142],
  ['A1390', 'A1300', 'Back wall', 'Back Wall', 8, 'A1624+3', 'Abutment A2 back wall', null],
  ['A1395', 'A1300', 'Backfill and approach slab', 'Approach Slab', 12, 'A1390+7', 'Abutment A2 backfill', null],

  ['A1600', 'A1000', 'Superstructure'],
  ['A1610', 'A1600', 'Span 1 girders — stress, cast and release', 'Girder', 18, 'A1140+4', 'Span 1 girders (yard)', 61],
  ['A1612', 'A1600', 'Span 2 girders — stress, cast and release', 'Girder', 18, 'A1610:SS+15', 'Span 2 girders (yard)', 73],
  ['A1614', 'A1600', 'Span 3 girders — stress, cast and release', 'Girder', 18, 'A1612:SS+15', 'Span 3 girders (yard)', 91],
  ['A1620', 'A1600', 'Span 1 girders — erect', 'Girder', 4, 'A1280 A1460 A1450+28 A1610', 'Span 1 girders', 149],
  ['A1622', 'A1600', 'Span 2 girders — erect', 'Girder', 4, 'A1620 A1560 A1550+28 A1612', 'Span 2 girders', null],
  ['A1624', 'A1600', 'Span 3 girders — erect', 'Girder', 4, 'A1622 A1380 A1614', 'Span 3 girders', null],
  ['A1630', 'A1600', 'Diaphragms, span 1', 'Diaphragm', 8, 'A1620', 'Span 1 diaphragms', null],
  ['A1632', 'A1600', 'Diaphragms, span 2', 'Diaphragm', 6, 'A1622 A1630', 'Span 2 diaphragms', null],
  ['A1634', 'A1600', 'Diaphragms, span 3', 'Diaphragm', 6, 'A1624 A1632', 'Span 3 diaphragms', null],
  ['A1640', 'A1600', 'Deck slab, span 1', 'Deck', 12, 'A1630+7', 'Span 1 deck slab', null],
  ['A1642', 'A1600', 'Deck slab, span 2', 'Deck', 10, 'A1632+7 A1640:SS+7', 'Span 2 deck slab', null],
  ['A1644', 'A1600', 'Deck slab, span 3', 'Deck', 10, 'A1634+7 A1642:SS+7', 'Span 3 deck slab', null],
  ['A1650', 'A1600', 'Barriers and footways', 'Side Wall', 16, 'A1644+10', 'Barriers and footways', null],

  ['A2000', 'A1000', 'Approaches and slope protection'],
  ['A2010', 'A2000', 'Approach embankments and pavement', 'Pavement', 24, 'A1295 A1395', 'Approaches', null],
  ['A2020', 'A2000', 'Slope protection at A1 and A2, aprons at P1 and P2', 'River Training', 21, 'A1624', 'Slope protection', null],
  ['A2030', 'A2000', 'Drainage and lighting', 'Services', 10, 'A1650', 'Drainage and lighting', null],

  ['A1900', 'A1000', 'Finishes and handover'],
  ['A1910', 'A1900', 'Expansion joints', 'Expansion Joint', 8, 'A1644+14', 'Expansion joints', null],
  ['A1920', 'A1900', 'Wearing course', 'Road Surface', 4, 'A1644+28 A1910 A2010', 'Wearing course', null],
  ['A1930', 'A1900', 'Road markings and furniture', 'Lane Divider', 3, 'A1920', 'Road markings', null],
  ['A1940', 'A1900', 'Load test', 'Test', 2, 'A1930+1', 'Load test', null],
  ['A1950', 'A1900', 'Traffic switch and removal of the detour', 'Temporary Works', 10, 'A1940', 'Temporary detour removal', null],
  ['A1955', 'A1900', 'Removal of the causeway', 'Temporary Works', 6, 'A1624 A2020', 'Temporary causeway removal', null],
  ['A1960', 'A1900', 'Handover and demobilisation', 'Preliminaries', 7, 'A1950:SS+3 A1955', 'Handover', null],
];

function calendar(): WorkCalendar {
  return { id: `${SITE_PREFIX}calendar-adl`, name: 'Site, seven days', week: '1111111', holidays: [], windows: [], provisional: true };
}

function schedule(start: string): SerialisedSchedule {
  const leaves = PROGRAMME.filter(isLeaf);
  const links: NonNullable<SerialisedSchedule['links']> = [];
  const pinned = new Map<string, number>();
  for (const [id, , , , , logic] of leaves) {
    for (const token of logic.split(/\s+/).filter(Boolean)) {
      const at = /^@(\d+)$/.exec(token);
      if (at) {
        pinned.set(id, Number(at[1]));
        continue;
      }
      const m = /^(A\d+)(?::(FS|SS|FF|SF))?([+-]\d+)?$/.exec(token)!;
      links.push({ from: m[1]!, to: id, type: m[2] ?? 'FS', lagDays: Number(m[3] ?? 0) });
    }
  }
  return {
    projectStartIso: start,
    activities: PROGRAMME.map((r) => ({
      id: r[0],
      name: r[2],
      parentId: r[1],
      children: PROGRAMME.filter((c) => c[1] === r[0]).map((c) => c[0]),
      durationDays: isLeaf(r) ? r[4] : 0,
      manualStartDays: pinned.get(r[0]) ?? null,
      componentKind: isLeaf(r) ? r[3] : '',
    })),
    links,
  };
}

function contract(): ContractHeader {
  return {
    contractor: 'Agusan Bridgeworks Corp.',
    contractNo: 'DMRP-A3-CW-07',
    value: 412_800_000,
    currency: 'PHP',
    commencement: day(0),
    completionDays: 240,
    milestones: [
      { dueOn: day(33), name: 'Traffic on the detour' },
      { dueOn: day(140), name: 'Substructure complete' },
      { dueOn: day(185), name: 'Deck complete' },
      { dueOn: day(239), name: 'Taking over' },
    ],
  };
}

/**
 * The baseline's activities, each with the component its row names — what the
 * Scheduler's Simulate reads for the place, and for an activity off the
 * structure: "Span 1 girders (yard)" casts girders, it does not put them up.
 */
function withComponents(activities: BaselineActivity[], leaves: readonly Leaf[]): BaselineActivity[] {
  const named = new Map(leaves.map((r) => [r[0], r[6]]));
  return activities.map((a) => (named.has(a.activityId) ? { ...a, component: named.get(a.activityId)! } : a));
}

/** The frozen baseline and its matches, before sliding. */
function adlayanProgramme(): { baseline: Baseline; matches: ScheduleMatch[]; extensions: ContractExtension[] } {
  const cal = calendar();
  const s = schedule(day(0));
  const works = adlayanWorks();
  const baseline: Baseline = {
    id: `${SITE_PREFIX}baseline-adl`,
    bridgeId: ADLAYAN_ID,
    name: 'Contract programme, Rev 0',
    projectStart: day(0),
    modelId: null,
    takenBy: PEOPLE.planner,
    takenAt: `${day(-6)}T01:00:00Z`,
    note: '',
    activities: withComponents(baselineFrom(s, cal, day(0)), PROGRAMME.filter(isLeaf)),
    state: 'accepted',
    reason: null,
    acceptedBy: PEOPLE.approver,
    acceptedAt: `${day(-3)}T02:00:00Z`,
    calendarId: cal.id,
    calendar: cal,
    links: baselineLinks(s),
    contract: contract(),
  };
  const matches: ScheduleMatch[] = PROGRAMME.filter(isLeaf).map(([id, , name, , , , component, doneOn], i) => ({
    id: `${SITE_PREFIX}match-adl-${String(i + 1).padStart(3, '0')}`,
    bridgeId: ADLAYAN_ID,
    activityId: id,
    activityName: name,
    component,
    componentId: idForLabel(works, component),
    completedOn: doneOn === null ? null : day(doneOn),
    matchedBy: PEOPLE.planner,
    matchedAt: `${day(0)}T01:30:00Z`,
  }));
  const extensions: ContractExtension[] = [
    {
      id: `${SITE_PREFIX}eot-adl-1`,
      bridgeId: ADLAYAN_ID,
      reason: 'Adlayan Creek in flood over the A2 and P2 cofferdams; work at A2 and on the causeway suspended.',
      days: 4,
      state: 'accepted',
      raisedBy: PEOPLE.planner,
      raisedAt: `${day(70)}T01:00:00Z`,
      decidedBy: PEOPLE.approver,
      decidedAt: `${day(78)}T03:00:00Z`,
    },
    {
      id: `${SITE_PREFIX}eot-adl-2`,
      bridgeId: ADLAYAN_ID,
      reason: 'Elastomeric bearings delivered a month late by the nominated supplier; girder erection held.',
      days: 30,
      state: 'proposed',
      raisedBy: PEOPLE.planner,
      raisedAt: `${day(110)}T01:00:00Z`,
      decidedBy: null,
      decidedAt: null,
    },
  ];
  return { baseline, matches, extensions };
}

/* ===========================================================================
   WHAT ./fwo-demo.ts READS — every one empty unless the flag is set.
   =========================================================================== */

const OVERLAYS: Readonly<Record<string, Partial<Bridge>>> = {
  [ADLAYAN_ID]: ADLAYAN,
  [AFGA_ID]: AFGA,
};

/** Fields the overlay sets whatever the register holds: the phase is the demonstration. */
const OWNED: ReadonlySet<string> = new Set([
  'phase', 'restricted', 'phaseAt', 'phaseBy', 'phaseOn', 'phaseCause', 'posting', 'loadRestriction', 'inServiceOn',
]);

const blank = (v: unknown) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

/**
 * The register with the two records filled in, in place of nothing it holds.
 * A record missing from `bridges` is not added: these are DPWH's own structures,
 * and the overlay has nothing to stand on without the row.
 */
export function withDpwhDemo(tenant: string, bridges: readonly Bridge[]): Bridge[] {
  if (!dpwhDemoEnabled() || tenant !== TENANT) return [...bridges];
  return bridges.map((b) => {
    const overlay = OVERLAYS[b.id];
    if (!overlay) return b;
    const out: Record<string, unknown> = { ...b };
    const raw = b as unknown as Record<string, unknown>;
    for (const [key, value] of Object.entries(overlay)) {
      if (OWNED.has(key) || blank(raw[key])) out[key] = value;
    }
    if (b.id === ADLAYAN_ID) {
      const latest = dpwhPhaseFor(tenant, b.id).at(-1);
      if (latest) {
        out['phaseAt'] = latest.at;
        out['phaseBy'] = latest.by;
      }
    }
    return out as unknown as Bridge;
  });
}

/** True where the overlay supplied this field because the listing leaves it empty. */
export function isDpwhDemoField(bridgeId: string, field: string): boolean {
  if (!dpwhDemoEnabled()) return false;
  const overlay = OVERLAYS[bridgeId] as Record<string, unknown> | undefined;
  if (!overlay || !(field in overlay)) return false;
  const listed = BRIDGES_DPWH.find((b) => b.id === bridgeId) as unknown as Record<string, unknown> | undefined;
  return listed === undefined || blank(listed[field]);
}

/** Whether the demonstration's phase history stands in for the register's. */
export function dpwhOwnsPhase(tenant: string, bridgeId: string): boolean {
  return dpwhDemoEnabled() && tenant === TENANT && bridgeId in OVERLAYS;
}

/** Whether a row came out of this file — read off the id, as ./fwo-demo.ts does. */
export function isDpwhDemoRow(kind: 'works' | 'site' | 'rating', id: string): boolean {
  return id.startsWith(kind === 'works' ? WORKS_PREFIX : kind === 'site' ? SITE_PREFIX : 'demo-rating-');
}

export function dpwhDefectsFor(tenant: string): RegisterDefect[] {
  if (!dpwhDemoEnabled() || tenant !== TENANT) return [];
  return AFGA_DEFECTS.map((d) => ({ ...d }));
}

function allWorks(): ProgressEntry[] {
  const days = shift();
  return adlayanWorks().map((e) => slide(e, days));
}

export function dpwhProgressFor(tenant: string, bridgeId: string): ProgressEntry[] {
  if (!dpwhDemoEnabled() || tenant !== TENANT || bridgeId !== ADLAYAN_ID) return [];
  return allWorks();
}

export function dpwhProgressInPeriod(tenant: string, from: string, to: string): ProgressEntry[] {
  if (!dpwhDemoEnabled() || tenant !== TENANT) return [];
  return allWorks().filter((e) => e.occurredOn >= from && e.occurredOn <= to);
}

export function dpwhSiteRecordFor(
  tenant: string,
  bridgeId: string,
): { returns: SiteReturn[]; incidents: SiteIncident[]; documents: SiteDocument[]; tests: SiteTest[]; items: SiteItem[] } | null {
  if (!dpwhDemoEnabled() || tenant !== TENANT || bridgeId !== ADLAYAN_ID) return null;
  const days = shift();
  const s = (iso: string) => addDays(iso, days);
  const newestFirst = <T>(on: (x: T) => string) => (a: T, b: T) => on(b).localeCompare(on(a));
  const works = allWorks();
  const tests = adlayanTests(works);
  return {
    returns: adlayanReturns(adlayanWorks())
      .map((r) => ({ ...r, returnedOn: s(r.returnedOn), createdAt: s(r.createdAt), updatedAt: s(r.updatedAt), reviewedAt: r.reviewedAt === null ? null : s(r.reviewedAt) }))
      .sort(newestFirst((r) => r.returnedOn)),
    incidents: adlayanIncidents()
      .map((r) => ({ ...r, occurredOn: s(r.occurredOn), closedOn: r.closedOn ? s(r.closedOn) : null, createdAt: s(r.createdAt), updatedAt: s(r.updatedAt) }))
      .sort(newestFirst((r) => r.occurredOn)),
    documents: adlayanDocuments(works)
      .map((d) => ({ ...d, datedOn: d.datedOn === null ? null : s(d.datedOn), createdAt: s(d.createdAt) }))
      .sort(newestFirst((d) => d.createdAt)),
    tests: tests
      .map((t) => ({ ...t, testedOn: s(t.testedOn), createdAt: s(t.createdAt) }))
      .sort(newestFirst((t) => t.testedOn)),
    items: adlayanItems(tests)
      .map((i) => ({
        ...i,
        dueOn: i.dueOn ? s(i.dueOn) : null,
        raisedAt: s(i.raisedAt),
        answeredAt: i.answeredAt ? s(i.answeredAt) : null,
        closedAt: i.closedAt ? s(i.closedAt) : null,
        updatedAt: s(i.updatedAt),
      }))
      .sort(newestFirst((i) => i.raisedAt)),
  };
}

export function dpwhScheduleFor(
  tenant: string,
  bridgeId: string,
): { baseline: Baseline; matches: ScheduleMatch[]; extensions: ContractExtension[] } | null {
  if (!dpwhDemoEnabled() || tenant !== TENANT || bridgeId !== ADLAYAN_ID) return null;
  const days = shift();
  const { baseline, matches, extensions } = adlayanProgramme();
  const c = baseline.contract!;
  return {
    baseline: {
      ...baseline,
      projectStart: addDays(baseline.projectStart, days),
      takenAt: addDays(baseline.takenAt, days),
      acceptedAt: baseline.acceptedAt ? addDays(baseline.acceptedAt, days) : null,
      activities: baseline.activities.map((a) => ({
        ...a,
        plannedStart: addDays(a.plannedStart, days),
        plannedFinish: addDays(a.plannedFinish, days),
        ...(a.notBefore ? { notBefore: addDays(a.notBefore, days) } : {}),
      })),
      contract: { ...c, commencement: addDays(c.commencement, days), milestones: c.milestones.map((m) => ({ ...m, dueOn: addDays(m.dueOn, days) })) },
    },
    matches: matches.map((m) => ({ ...m, completedOn: m.completedOn === null ? null : addDays(m.completedOn, days), matchedAt: addDays(m.matchedAt, days) })),
    extensions: extensions.map((e) => ({ ...e, raisedAt: addDays(e.raisedAt, days), decidedAt: e.decidedAt === null ? null : addDays(e.decidedAt, days) })),
  };
}

export function dpwhPhaseFor(tenant: string, bridgeId: string): PhaseTransition[] {
  if (!dpwhDemoEnabled() || tenant !== TENANT) return [];
  let rows: Omit<PhaseTransition, 'id' | 'bridgeId' | 'seq'>[] = [];
  if (bridgeId === ADLAYAN_ID) {
    const days = shift();
    const { baseline } = adlayanProgramme();
    rows = [
      { fromPhase: null, toPhase: 'planned', restricted: false, at: addDays(`${day(-60)}T01:00:00Z`, days), by: PEOPLE.planner, reason: null, causeKind: 'create', causeId: null },
      { fromPhase: 'planned', toPhase: 'construction', restricted: false, at: addDays(baseline.acceptedAt!, days), by: PEOPLE.approver, reason: null, causeKind: 'baseline', causeId: baseline.id, effectiveOn: addDays(day(0), days) },
    ];
  } else if (bridgeId === AFGA_ID) {
    const rating = afgaRating();
    rows = [
      { fromPhase: null, toPhase: 'in-service', restricted: false, at: '2024-03-04T02:00:00Z', by: PEOPLE.engineer, reason: null, causeKind: 'create', causeId: null, inServiceOn: '1958' },
      {
        fromPhase: 'in-service', toPhase: 'in-service', restricted: true, at: rating.acceptedAt!, by: rating.acceptedBy,
        reason: `Posted ${postingTonnes(rating.result)} t by accepted rating ${rating.id}.`, causeKind: 'rating', causeId: rating.id,
      },
    ];
  }
  return rows.map((row, i) => ({ ...row, id: `${SITE_PREFIX}phase-${bridgeId}-${i + 1}`, bridgeId, seq: i + 1 }));
}

export function dpwhRatingsFor(tenant: string): RatingRecord[] {
  if (!dpwhDemoEnabled() || tenant !== TENANT) return [];
  return [afgaRating()];
}
